import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { unpack, pack } from 'msgpackr';
import stringify from '../utils/stableStringify';

import Form, { Item } from 'devextreme-react/form';
import AntTime from '../utils/AntTime';
import AntConfig from '../utils/AntConfig';
import AntLocalStorage from '../utils/AntLocalStorage';
import AntCondition from '../utils/AntCondition';
import AntLocalization from '../utils/AntLocalization';
import { ValidationGroup } from 'devextreme-react/validation-group';
import { Validator } from 'devextreme-react/validator';
import { Template } from 'devextreme-react/core/template';
import { TextBox, CheckBox, SelectBox, TextArea, NumberBox, DateBox } from 'devextreme-react';
import AntSelectBox from './AntSelectBox';
import AntTagBox from './AntTagBox';
import AntDropDownGrid from './AntDropDownGrid';
import AntDropDownTree from './AntDropDownTree';
import AntMonthBox from './AntMonthBox';
import AntYearBox from './AntYearBox';
import AntAddress from '../utils/AntAddress';
import AntCategory from '../utils/AntCategory';

import { AntGlobal, AntSession } from '../utils/AntContext';

export default function (props) {
    const appGlobal = AntGlobal();
    // const session = AntSession();

    const tableName = props.table ? props.table : props.API ? props.API.entityName : '';

    const config = useMemo(() => {
        let found = appGlobal.tableConfigs?.find((t) => t.tableName === tableName);
        if (found) return found.config;

        let cnf = new AntConfig(appGlobal, tableName);
        cnf.configFields(props.fields);

        if (appGlobal.tableConfigs instanceof Array === false) appGlobal.tableConfigs = [];

        appGlobal.tableConfigs.push({
            tableName: tableName,
            config: cnf
        });
        return cnf;
    }, []);

    // Theo ngôn ngữ business (đồng bộ toàn app), KHÔNG getSetting (đọc user_settings trước → lệch tiếng Anh).
    const addrCountry = AntLocalStorage.getBusinessSetting('Sys_BaseCountry') || 'VN';
    const addrLocale = AntLocalStorage.getBusinessSetting('Sys_BaseLanguage') || 'vi';
    const [addrLoadTick, setAddrLoadTick] = useState(0);
    const AddressData = useMemo(() => new AntAddress(addrCountry, addrLocale), [addrCountry, addrLocale, addrLoadTick]);
    useEffect(() => {
        AntAddress.ensure(addrCountry, props.API).then(() => setAddrLoadTick((n) => n + 1));
    }, [addrCountry]);
    const CategoryData = useMemo(() => new AntCategory(appGlobal.appAssets.data.categoryData), []);

    const [colCount, setColCount] = useState(props.colCount ? props.colCount : props.colCountDefault ? props.colCountDefault : 1);
    const [prepared, setPrepared] = useState(false);
    const [, setAddrRenderTick] = useState(0);

    // const formItemRefs = useRef(props.items.map((i) => useRef()));
    const formItemRefs = useMemo(() => props.items.map((i) => React.createRef()), []);

    const formItems = useRef([]);

    const [formData, setFormData] = useState(props.formData ? props.formData : {});

    const formLookups = useRef([]);

    const form = useRef();
    const onFieldDataChangedRef = useRef(null);

    const load = (defaultObj) => {
        return new Promise(async (resolve) => {
            if (defaultObj === undefined || defaultObj === null) defaultObj = {};

            var obj = initDefaultValues();
            if (obj)
                Object.keys(obj).forEach(function (key) {
                    if (defaultObj[key] !== undefined) defaultObj[key] = obj[key];
                });

            if (props.default)
                Object.keys(props.default).forEach(function (key) {
                    defaultObj[key] = props.default[key];
                });

            Object.keys(defaultObj).map((k) => {
                if (defaultObj[k] !== null && typeof defaultObj[k] === 'object') defaultObj[k] = AntCondition.getValueFromSession(defaultObj[k]);
            });

            if (props.prepare !== false && props.API && props.API.postSearchPrepare) {
                obj = await props.API.postSearchPrepare(props.prepareConditions);

                if (obj)
                    Object.keys(obj).forEach(function (key) {
                        defaultObj[key] = obj[key];
                    });
            }
            if (props.formData)
                Object.keys(props.formData).forEach(function (key) {
                    defaultObj[key] = props.formData[key];
                });

            if (!formLookups.current || formLookups.current instanceof Array === false || formLookups.current.length <= 0)
                formLookups.current = obj && obj.lookups instanceof Array ? obj.lookups : [];
            defaultObj = JSON.parse(stringify({ ...props.defaultObj, ...defaultObj }));
            delete defaultObj.lookups;

            setFormData((v) => Object.assign(v, defaultObj));

            setPrepared(true);

            if (props.onPrepared) props.onPrepared(defaultObj);

            resolve(formData);
        });
    };

    const initDefaultValues = () => {
        var obj = {};

        if (props.items instanceof Array) {
            for (let i = 0; i < props.items.length; i++) {
                const item = props.items[i];
                var defaultValue = getDefaultFieldValue(item);
                if (defaultValue !== undefined) {
                    if (typeof defaultValue === 'object' && defaultValue !== null && defaultValue.value !== undefined) {
                        obj[item.dataField] = defaultValue.value;
                        if (String(item.dataField).endsWith('_code')) {
                            var displayField = String(item.dataField).replace('_code', '_name');
                            obj[displayField] = defaultValue.display;
                        }
                    } else obj[item.dataField] = defaultValue;
                }
            }
        }
        return obj;
    };

    const setField = useCallback((field, value, raiseEvent) => {
        if (!formData) return;

        var previousValue = formData[field];
        setFormData((v) => Object.assign(v, { [field]: value }));

        if (raiseEvent !== false)
            onFieldDataChangedRef.current?.({
                [field]: value,
                oldValue: previousValue
            });
    }, [formData]);

    const getField = useCallback((field) => {
        return formData[field];
    }, [formData]);

    const getFormData = () => {
        var obj = formData;
        if (!obj) return null;

        obj = JSON.parse(stringify(obj));
        Object.keys(obj).map((k) => {
            if (obj[k] === null || obj[k] === '') obj[k] = undefined;
        });

        Object.keys(obj).forEach((key) => {
            if (obj[key] === undefined) {
                delete obj[key];
            }
        });
        return obj;
    };

    const isDisableField = useCallback((item) => {
        let visible = isVisibleField(item);
        if (!visible) return true;

        var disabled =
            !item ||
            !formData ||
            (formData.search && formData.search.disable_fields instanceof Array && formData.search.disable_fields.indexOf(item.dataField) > -1);

        if (disabled) return true;

        if (props.items instanceof Array) {
            var obj = props.items.find((e) => e.dataField === item.dataField);
            if (obj) {
                if (obj.disable === true || obj.disabled === true) return true;
                if (obj.disable === false || obj.disabled === false) return false;

                if (obj.editable === true || obj.editabled === true) return false;
                if (obj.editable === false || obj.editabled === false) return true;

                if (typeof obj.disable === 'object' && obj.disable !== null) {
                    if (obj.disable.type === 'self') {
                        if (obj.disable.self) {
                            var selfType = obj.disable.self.type;
                            var selfValue = formData[item.dataField];
                            var values = obj.disable.self.values;
                            var value = obj.disable.self.value;

                            if (selfType === 'diff') {
                                return values instanceof Array ? values.indexOf(selfValue) <= -1 : selfValue !== value;
                            } else {
                                return values instanceof Array ? values.indexOf(selfValue) >= 0 : selfValue === value;
                            }
                        }
                    } else return AntCondition.isDisable(formData, obj);
                }

                if (typeof obj.editable === 'object' && obj.editable !== null) {
                    if (obj.editable.type === 'self') {
                        if (obj.editable.self) {
                            var selfType = obj.editable.self.type;
                            var selfValue = formData[item.dataField];
                            var values = obj.editable.self.values;
                            var value = obj.editable.self.value;

                            if (selfType === 'diff') {
                                return values instanceof Array ? values.indexOf(selfValue) >= 0 : selfValue === value;
                            } else {
                                return values instanceof Array ? values.indexOf(selfValue) <= -1 : selfValue !== value;
                            }
                        }
                    } else return !AntCondition.isEditable(formData, obj);
                }
            }
        }

        if (config) {
            var obj = config.getCnfField(item.cnfField ? item.cnfField : item.dataField);

            if (!obj) return false;

            if (obj.disable === true || obj.disabled === true) return true;
            if (obj.disable === false || obj.disabled === false) return false;

            if (obj.editable === true || obj.editabled === true) return false;
            if (obj.editable === false || obj.editabled === false) return true;

            if (typeof obj.disable === 'object' && obj.disable !== null) {
                if (obj.disable.type === 'self') {
                    if (obj.disable.self) {
                        var selfType = obj.disable.self.type;
                        var selfValue = formData[item.dataField];
                        var values = obj.disable.self.values;
                        var value = obj.disable.self.value;

                        if (selfType === 'diff') {
                            return values instanceof Array ? values.indexOf(selfValue) <= -1 : selfValue !== value;
                        } else {
                            return values instanceof Array ? values.indexOf(selfValue) >= 0 : selfValue === value;
                        }
                    }
                } else return AntCondition.isDisable(formData, obj);
            }

            if (typeof obj.editable === 'object' && obj.editable !== null) {
                if (obj.editable.type === 'self') {
                    if (obj.editable.self) {
                        var selfType = obj.editable.self.type;
                        var selfValue = formData[item.dataField];
                        var values = obj.editable.self.values;
                        var value = obj.editable.self.value;

                        if (selfType === 'diff') {
                            return values instanceof Array ? values.indexOf(selfValue) >= 0 : selfValue === value;
                        } else {
                            return values instanceof Array ? values.indexOf(selfValue) <= -1 : selfValue !== value;
                        }
                    }
                } else return !AntCondition.isEditable(formData, obj);
            }
        }

        return false;
    });

    const isVisibleField = useCallback((item) => {
        if (!formData) return true;

        var visible = AntCondition.isVisible(formData, item);
        if (visible === false) return false;

        var rawItem;
        if (props.items instanceof Array) rawItem = props.items.find((e) => e.dataField === item.dataField);
        if (!rawItem) rawItem = item;

        if (visible && rawItem.visibleDepends instanceof Array) {
            for (let i = 0; i < rawItem.visibleDepends.length; i++) {
                const d = rawItem.visibleDepends[i];
                if (visible && d.dataField) {
                    var secondItem = props.items.find((e) => d.dataField === e.dataField);
                    if (secondItem) {
                        if (d.visible !== undefined) {
                            var v2 = isVisibleField(secondItem) === d.visible;
                            visible = visible && v2;
                        }
                        if (d.editable !== undefined) {
                            var v2 = !isDisableField(secondItem) === d.editable;
                            visible = visible && v2;
                        }
                    }
                }
            }
        }

        var tableName = rawItem.cnfTable ? rawItem.cnfTable : props.table ? props.table : props.API ? props.API.entityName : null;
        var strField = rawItem.cnfField ? rawItem.cnfField : rawItem.dataField;
        var cnfItem = config.getCnfField(strField, tableName);

        if (visible) {
            if (cnfItem && cnfItem.visibleDepends instanceof Array) {
                for (let i = 0; i < cnfItem.visibleDepends.length; i++) {
                    const d = cnfItem.visibleDepends[i];
                    if (visible && d.dataField) {
                        var secondItem = props.items.find((e) => d.dataField === e.dataField);
                        if (secondItem) {
                            if (d.visible !== undefined) {
                                var v2 = isVisibleField(secondItem) === d.visible;
                                visible = visible && v2;
                            }
                            if (d.editable !== undefined) {
                                var v2 = !isDisableField(secondItem) === d.editable;
                                visible = visible && v2;
                            }
                        }
                    }
                }
            }
        }

        if (visible) visible = AntCondition.isVisible(formData, cnfItem);

        return visible;
    });

    const getDefaultFieldValue = (item) => {
        if (props.items instanceof Array) {
            var rawItem = props.items.find((e) => e.dataField === item.dataField);
            if (rawItem) {
                var value = AntCondition.getDefault(formData, rawItem);
                if (value !== undefined && value !== null) return value;
            }
        }

        var cnfItem = config.getCnfField(item.cnfField ? item.cnfField : item.dataField);
        if (cnfItem) {
            var value = AntCondition.getDefault(formData, cnfItem);
            if (value !== undefined && value !== null) return value;
        }
        return undefined;
    };

    const initFormItems = useCallback(() => {
        var items = [];

        for (let i = 0; i < props.items.length; i++) {
            var propItem = props.items[i];

            propItem = JSON.parse(stringify(propItem));
            propItem.rItem = JSON.parse(stringify(propItem));

            var colSpan = AntCondition.getValue(formData, propItem.colSpan);
            if (colSpan === undefined) colSpan = propItem.colSpanDefault;

            var colCount = AntCondition.getValue(formData, propItem.colCount);
            if (colCount === undefined) colCount = propItem.colCountDefault;

            if (propItem.itemType === 'empty') {
                items.push({ ...propItem });
                continue;
            }

            var showLabel = props.showLabel !== undefined ? props.showLabel : false;

            var item = {
                ...propItem,
                colSpan: colSpan,
                colCount: colCount,
                template: 'renderItem',
                label: { visible: showLabel },
                placeholder:
                    props.showPlaceholder !== false && !showLabel && propItem.label
                        ? AntLocalization.translateText(AntCondition.getValue({}, propItem.label))
                        : null,
                type: propItem.type ? propItem.type : null,
                dataType: propItem.dataType ? String(propItem.dataType) : null,
                decimalDigit: propItem.decimalDigit !== undefined ? propItem.decimalDigit : null,
                lookup: propItem.lookup ? propItem.lookup : null
            };

            if (item.dataField || item.cnfField || item.cnfTable) {
                var tableName = item.cnfTable ? item.cnfTable : props.table ? props.table : props.API ? props.API.entityName : null;

                var strField = item.cnfField ? item.cnfField : item.dataField;
                item.fieldConfig = config.getCnfField(strField, tableName);

                // if (item.fieldConfig && item.fieldConfig.cnfTable) {
                //     var tableName = item.fieldConfig.cnfTable;

                //     var strField = item.fieldConfig.cnfField ? item.fieldConfig.cnfField : item.dataField;
                //     item.fieldConfig = config.getCnfField(strField, tableName);
                // }
            }

            var rItem = item.rItem;
            var editorName = rItem.editorName;
            var editorType = rItem.editorType;

            item.editorConfig = null;

            if (editorName && (typeof editorName === 'object' || editorName instanceof Array))
                editorName = AntCondition.getValue(formData, editorName);

            if (editorType && (typeof editorType === 'object' || editorType instanceof Array))
                editorType = AntCondition.getValue(formData, editorType);

            if (editorName) {
                item.editorConfig = config.getCnfEditor(editorName);

                if (item.editorConfig) {
                    editorName = item.editorConfig.name;
                    editorType = item.editorConfig.type;
                }
            }

            // if (!editorType) {
            if (
                (item.fieldConfig && item.fieldConfig.lookup && item.fieldConfig.lookup.values instanceof Array) ||
                (item.options && item.options.values instanceof Array)
            )
                editorType = 'antSelectBox';
            // }

            item.editorType = editorType;
            item.editorName = editorName;

            if (item.options && item.options instanceof Array) {
                item.options = AntCondition.getValue(formData, item.options, item.dataField);
            }
            if (!item.options) item.options = {};
            if (item.underlined) item.options.stylingMode = 'underlined';

            var classes = [];
            if (item.bold) classes.push('ant-bold-editor');
            if (item.upperCase) classes.push('ant-upper-case-editor');
            if (item.lowerCase) classes.push('ant-lower-case-editor');
            if (item.alignment === 'right') classes.push('ant-align-right-editor');
            if (item.alignment === 'left') classes.push('ant-align-left-editor');
            if (item.alignment === 'center') classes.push('ant-align-center-editor');
            if (classes.length > 0) item.options.className = classes.join(' ');

            if (propItem.label) {
                if (propItem.label instanceof Array) {
                    var txtLabel = AntCondition.getValue(formData, propItem.label);
                    txtLabel = AntLocalization.translateText(txtLabel);
                    item.label.text = txtLabel;
                } else item.label.text = AntLocalization.translateText(AntCondition.getValue({}, propItem.label));
            }

            if (item.fieldConfig) {
                if (item.fieldConfig.label) {
                    if (!item.label) item.label = { text: AntLocalization.translateText(AntCondition.getValue({}, item.fieldConfig.label)) };
                    else if (!item.label.text) item.label.text = AntLocalization.translateText(AntCondition.getValue({}, item.fieldConfig.label));
                }
                if (props.showPlaceholder !== false && !item.placeholder && !showLabel) {
                    // Dynamic lookup = lookup without static values (queries API) → prefix "Chọn"
                    // Static lookup = lookup.values array (status, type...) → no prefix
                    let _isDynamic = !!(item.fieldConfig.lookup && !item.fieldConfig.lookup.values) || item.fieldConfig.dataType === 'Array';
                    let _label = item.fieldConfig.placeholder
                        ? AntLocalization.translateText(AntCondition.getValue({}, item.fieldConfig.placeholder))
                        : item.fieldConfig.label
                        ? AntLocalization.translateText(AntCondition.getValue({}, item.fieldConfig.label))
                        : null;
                    item.placeholder = _label
                        ? (_isDynamic ? AntLocalization.translateText({ vi: 'Chọn ', en: 'Select ', fr: 'Sélectionner ', es: 'Seleccionar ', id: 'Pilih ', ja: '選択 ', zh: '选择 ', ko: '선택 ', hi: 'चुनें ', ar: 'اختر ' }) + _label.toLowerCase() : _label)
                        : (_isDynamic ? AntLocalization.translateText({ vi: 'Chọn...', en: 'Select...', fr: 'Choisir...', es: 'Seleccionar...', id: 'Pilih...', ja: '選択...', zh: '选择...', ko: '선택...', hi: 'चुनें...', ar: 'اختر...' }) : '...');
                }
                if (!item.type) item.type = item.fieldConfig.type;
                if (!item.dataType) item.dataType = item.fieldConfig.dataType;
                if (item.decimalDigit === undefined || item.decimalDigit === null) item.decimalDigit = item.fieldConfig.decimalDigit;
                if (!item.lookup) item.lookup = item.fieldConfig.lookup;
            }

            if (item.editorConfig) {
                if (item.editorConfig.label) {
                    if (!item.label) item.label = { text: AntLocalization.translateText(AntCondition.getValue({}, item.editorConfig.label)) };
                    else if (!item.label.text) item.label.text = AntLocalization.translateText(AntCondition.getValue({}, item.editorConfig.label));
                }
                if (props.showPlaceholder !== false && !item.placeholder && !showLabel) {
                    let _label = item.editorConfig.placeholder
                        ? AntLocalization.translateText(AntCondition.getValue({}, item.editorConfig.placeholder))
                        : item.editorConfig.label
                        ? AntLocalization.translateText(AntCondition.getValue({}, item.editorConfig.label))
                        : item.label && item.label.text
                        ? item.label.text
                        : null;
                    // editorConfig fields are select/lookup types in ANTE
                    item.placeholder = _label
                        ? AntLocalization.translateText({ vi: 'Chọn ', en: 'Select ', fr: 'Sélectionner ', es: 'Seleccionar ', id: 'Pilih ', ja: '選択 ', zh: '选择 ', ko: '선택 ', hi: 'चुनें ', ar: 'اختर ' }) + _label.toLowerCase()
                        : AntLocalization.translateText({ vi: 'Chọn...', en: 'Select...', fr: 'Choisir...', es: 'Seleccionar...', id: 'Pilih...', ja: '選択...', zh: '选择...', ko: '선택...', hi: 'चुनें...', ar: 'اختर...' });
                }
                if (!item.type) item.type = item.editorConfig.type;
                if (!item.dataType) item.dataType = item.editorConfig.dataType;
                if (item.decimalDigit === undefined || item.decimalDigit === null) item.decimalDigit = item.editorConfig.decimalDigit;
                // if (!item.lookup) item.lookup = item.editorConfig.lookup;
            }

            // Field adm hành chính: nhãn/placeholder lấy từ envelope nước (đa ngôn ngữ); giữ nhãn config
            // khi envelope chưa nạp hoặc cấp không có trong envelope (getLevelLabelText trả '').
            // Search form hiển thị placeholder (không hiện label) nên override cả hai.
            var admLevel = AntAddress.admLevelFromField(item.dataField);
            if (admLevel && AddressData.isLoaded()) {
                var admLabel = AddressData.getLevelLabelText(admLevel);
                if (admLabel) {
                    if (!item.label || typeof item.label !== 'object') item.label = { text: admLabel };
                    else item.label.text = admLabel;
                    if (props.showPlaceholder !== false) item.placeholder = admLabel;
                }
            }

            if (item.decimalDigit === undefined || item.decimalDigit === null)
                item.decimalDigit = AntLocalization.getCurrencyDecimalDigit();
            if (item.decimalDigit === undefined || item.decimalDigit === null) item.decimalDigit = 2;

            if (item.editorType === 'checkBox' || item.dataType === 'bool') {
                // if (props.labelLocation === 'right' || props.allCheckBoxLeft || item.checkBoxLeft) {
                item.label.oldText = AntLocalization.translateText(item.label.text);
                item.label.text = undefined;
                item.label.visible = false;
                // }
            }

            if (item.dataType === 'singleMonth') {
                items.push({
                    ...item,
                    dataField: item.dataField,
                    dataType: 'oneMonth',
                    format: item.format,
                    placeholder:
                        props.showPlaceholder !== false
                            ? propItem.placeholder
                                ? propItem.placeholder
                                : AntLocalization.translateText({ vi: AntLocalization.translateText({ vi: 'Tháng', en: 'Month', fr: 'Mois', es: 'Mes', id: 'Bulan', ja: '月', zh: '月', ko: '월', hi: 'महीना', ar: 'شهر' }), en: 'Month' })
                            : undefined
                });
            } else if (item.dataType === 'singleYear') {
                items.push({
                    ...item,
                    dataField: item.dataField,
                    dataType: 'oneYear',
                    format: item.format,
                    placeholder:
                        props.showPlaceholder !== false
                            ? propItem.placeholder
                                ? propItem.startLabel
                                : AntLocalization.translateText({ vi: 'Năm', en: 'Year', fr: 'Année', es: 'Año', id: 'Tahun', ja: '年', zh: '年', ko: '년', hi: 'वर्ष', ar: 'سنة' })
                            : undefined
                });
            } else if (item.dataType === 'oneMonth') {
                items.push({
                    ...item,
                    dataField: item.dataField + '_oneMonth',
                    dataType: 'oneMonth',
                    format: item.format,
                    placeholder:
                        props.showPlaceholder !== false
                            ? propItem.placeholder
                                ? propItem.placeholder
                                : AntLocalization.translateText({ vi: AntLocalization.translateText({ vi: 'Tháng', en: 'Month', fr: 'Mois', es: 'Mes', id: 'Bulan', ja: '月', zh: '月', ko: '월', hi: 'महीना', ar: 'شهر' }), en: 'Month' })
                            : undefined
                });
            } else if (item.dataType === 'oneYear') {
                items.push({
                    ...item,
                    dataField: item.dataField + '_oneYear',
                    dataType: 'oneYear',
                    format: item.format,
                    placeholder:
                        props.showPlaceholder !== false
                            ? propItem.placeholder
                                ? propItem.startLabel
                                : AntLocalization.translateText({ vi: 'Năm', en: 'Year', fr: 'Année', es: 'Año', id: 'Tahun', ja: '年', zh: '年', ko: '년', hi: 'वर्ष', ar: 'سنة' })
                            : undefined
                });
            } else if (item.dataType === 'month' || item.editorType === 'antMonthBox') {
                items.push({
                    ...item,
                    dataField: item.dataField + '_startMonth',
                    dataType: 'monthStart',
                    format: item.format,
                    placeholder:
                        props.showPlaceholder !== false
                            ? propItem.startLabel
                                ? propItem.startLabel
                                : AntLocalization.translateText({ vi: 'Từ tháng', en: 'From month', fr: 'Mois début', es: 'Desde mes', id: 'Dari bulan', ja: '開始月', zh: '从月份', ko: '시작 월', hi: 'महीने से', ar: 'من الشهر' })
                            : undefined
                });
                items.push({
                    ...item,
                    dataField: item.dataField + '_endMonth',
                    dataType: 'monthEnd',
                    format: item.format,
                    placeholder:
                        props.showPlaceholder !== false
                            ? propItem.endLabel
                                ? propItem.endLabel
                                : AntLocalization.translateText({ vi: 'Đến tháng', en: 'To month', fr: "Jusqu'au mois", es: 'Hasta mes', id: 'Sampai bulan', ja: '終了月', zh: '到月份', ko: '종료 월', hi: 'महीने तक', ar: 'إلى الشهر' })
                            : undefined
                });
            } else if (item.dataType === 'year') {
                items.push({
                    ...item,
                    dataField: item.dataField + '_startYear',
                    dataType: 'yearStart',
                    format: item.format,
                    placeholder:
                        props.showPlaceholder !== false
                            ? propItem.startLabel
                                ? propItem.startLabel
                                : AntLocalization.translateText({ vi: 'Từ năm', en: 'From year', fr: 'Année début', es: 'Desde año', id: 'Dari tahun', ja: '開始年', zh: '从年份', ko: '시작 년', hi: 'वर्ष से', ar: 'من السنة' })
                            : undefined
                });
                items.push({
                    ...item,
                    dataField: item.dataField + '_endYear',
                    dataType: 'yearEnd',
                    format: item.format,
                    placeholder:
                        props.showPlaceholder !== false
                            ? propItem.endLabel
                                ? propItem.endLabel
                                : AntLocalization.translateText({ vi: 'Đến năm', en: 'To year', fr: "Année fin", es: 'Hasta año', id: 'Sampai tahun', ja: '終了年', zh: '到年份', ko: '종료 년', hi: 'वर्ष तक', ar: 'إلى السنة' })
                            : undefined
                });
            } else if (item.dataType === 'date' || item.dataType === 'datetime') {
                items.push({
                    ...item,
                    dataField: item.dataField + '_startDate',
                    dataType: 'dateStart',
                    format: item.format,
                    placeholder:
                        props.showPlaceholder !== false
                            ? propItem.startLabel
                                ? propItem.startLabel
                                : AntLocalization.translateText({ vi: 'Từ ngày', en: 'From date', fr: 'Date début', es: 'Desde fecha', id: 'Dari tanggal', ja: '開始日', zh: '从日期', ko: '시작 날짜', hi: 'तारीख से', ar: 'من تاريخ' })
                            : undefined
                });
                items.push({
                    ...item,
                    dataField: item.dataField + '_endDate',
                    dataType: 'dateEnd',
                    format: item.format,
                    placeholder:
                        props.showPlaceholder !== false
                            ? propItem.endLabel
                                ? propItem.endLabel
                                : AntLocalization.translateText({ vi: 'Đến ngày', en: 'To date', fr: "Date fin", es: 'Hasta fecha', id: 'Sampai tanggal', ja: '終了日', zh: '到日期', ko: '종료 날짜', hi: 'तारीख तक', ar: 'إلى تاريخ' })
                            : undefined
                });
            } else items.push(item);
        }

        // items = items.filter((item) => {
        //     return AntCondition.isVisible(formData, item);
        // });

        for (let i = 0; i < items.length; i++) {
            var item = items[i];
            item.visible = true; //isVisibleField(item);
            item.disabled = isDisableField(item);
        }
        return items;
    });

    const resetVisibility = useCallback(() => {
        if (form.current && form.current.instance) {
            if (props.items instanceof Array) {
                for (let i = 0; i < props.items.length; i++) {
                    const item = props.items[i];
                    var visible = isVisibleField(item);
                    var edtName = item.name ? item.name : item.dataField;
                    if (edtName) {
                        var formItem = form.current.instance.itemOption(edtName);
                        if (formItem) {
                            var currentVisible = formItem.visible;
                            if (currentVisible !== visible) {
                                form.current.instance.itemOption(edtName, 'visible', visible);
                                if (props.onVisibleFieldChanged) props.onVisibleFieldChanged(edtName, visible);
                            }
                        }
                    }
                }
            }
        }
    });

    const resetDisabled = useCallback(() => {
        if (form.current && form.current.instance) {
            if (props.items instanceof Array) {
                for (let i = 0; i < props.items.length; i++) {
                    const item = props.items[i];
                    var disabled = isDisableField(item);

                    var edtName = item.name ? item.name : item.dataField;
                    if (edtName) {
                        var formItem = form.current.instance.itemOption(edtName);
                        if (formItem) {
                            var currentDisabled = formItem.disabled;
                            if (currentDisabled !== disabled) {
                                form.current.instance.itemOption(edtName, 'disabled', disabled);
                                if (props.onDisableFieldChanged) props.onDisableFieldChanged(edtName, disabled);
                            }
                        }
                    }
                }
            }
        }
    });

    const resetColSpan = useCallback(() => {
        var changed = false;

        if (form.current && form.current.instance) {
            if (formItems.current instanceof Array) {
                for (let i = 0; i < formItems.current.length; i++) {
                    const item = formItems.current[i];

                    var colSpan = AntCondition.getValue(formData, item.colSpan);
                    if (colSpan === undefined) colSpan = item.colSpanDefault;

                    var itemName = item.name ? item.name : item.dataField;
                    var itemPath = item && item.groupName ? item.groupName + '.' + itemName : null;
                    if (!itemPath) itemPath = item && item.groupCaption ? item.groupCaption + '.' + itemName : null;
                    if (!itemPath) itemPath = itemName;

                    if (itemPath) {
                        var formItem = form.current.instance.itemOption(itemPath);
                        if (formItem) {
                            var currentColSpan = formItem.colSpan;
                            if (currentColSpan !== colSpan) {
                                changed = true;
                                form.current.instance.itemOption(itemPath, 'colSpan', colSpan);
                            }
                        }
                    }
                }
            }
        }
        return changed;
    });

    const resetDefaultValue = useCallback(() => {
        if (props.items instanceof Array) {
            for (let i = 0; i < props.items.length; i++) {
                const item = props.items[i];
                if (formData[item.dataField] === undefined) {
                    var defaultValue = getDefaultFieldValue(item);
                    if (defaultValue !== undefined) {
                        let obj = {};
                        if (typeof defaultValue === 'object' && defaultValue !== null && defaultValue.value !== undefined) {
                            obj[item.dataField] = defaultValue.value;

                            if (String(item.dataField).endsWith('_code')) {
                                var displayField = String(item.dataField).replace('_code', '_name');
                                obj[displayField] = defaultValue.display;
                            }
                        } else obj[item.dataField] = defaultValue;
                        setFormData((v) => Object.assign(v, obj));

                        if (form.current && form.current.instance) {
                            var edtName = item.name ? item.name : item.dataField;
                            if (edtName) {
                                form.current.instance.itemOption(edtName, 'value', defaultValue);
                                if (props.onDefaultValueChanged) props.onDefaultValueChanged(edtName, defaultValue);
                            }
                        }
                    }
                }
            }
        }
    });

    const renderItem = useCallback((args) => {
        let item = formItems.current.find((i) =>
            args.name ? i.name === args.name && i.dataField === args.dataField : i.dataField === args.dataField
        );
        if (!item) return <div />;

        let itemIndex = formItems.current.findIndex((i) =>
            args.name ? i.name === args.name && i.dataField === args.dataField : i.dataField === args.dataField
        );

        let itemRef = formItemRefs[itemIndex];

        let fieldConfig = item.fieldConfig;

        if (!item.fieldConfig) {
            if (item.dataField || item.cnfField) {
                var tableName = item.cnfTable ? item.cnfTable : props.table ? props.table : props.API ? props.API.entityName : null;

                var strField = item.cnfField ? item.cnfField : item.dataField;
                item.fieldConfig = config.getCnfField(strField, tableName);

                // if (item.fieldConfig && item.fieldConfig.cnfTable) {
                //     var tableName = item.fieldConfig.cnfTable;

                //     var strField = item.fieldConfig.cnfField ? item.fieldConfig.cnfField : item.field;
                //     item.fieldConfig = config.getCnfField(strField, tableName);
                // }
            }
        }

        var remoteLookups =
            formLookups.current instanceof Array && formLookups.current.length > 0
                ? formLookups.current
                : formData && formData.lookups instanceof Array
                    ? formData.lookups
                    : [];

        var rItem = item.rItem;
        var editorName = rItem.editorName;
        var editorType = rItem.editorType;

        item.editorConfig = null;

        if (editorName && (typeof editorName === 'object' || editorName instanceof Array)) editorName = AntCondition.getValue(formData, editorName);

        if (editorType && (typeof editorType === 'object' || editorType instanceof Array)) editorType = AntCondition.getValue(formData, editorType);

        if (editorName) {
            item.editorConfig = config.getCnfEditor(editorName);

            if (item.editorConfig) {
                editorName = item.editorConfig.name;
                editorType = item.editorConfig.type;
            }
        }

        if (!editorType && item.fieldConfig && item.fieldConfig.editorType) editorType = item.fieldConfig.editorType;
        if (!editorName && item.fieldConfig && item.fieldConfig.editorName) editorName = item.fieldConfig.editorName;

        // if (!editorType) {
        if (
            (item.fieldConfig && item.fieldConfig.lookup && item.fieldConfig.lookup.values instanceof Array) ||
            (item.options && item.options.values instanceof Array)
        )
            editorType = 'antSelectBox';
        // }

        item.editorType = editorType;
        item.editorName = editorName;

        if (rItem.label) {
            if (rItem.label instanceof Array) {
                var txtLabel = AntCondition.getValue(formData, rItem.label);
                txtLabel = AntLocalization.translateText(txtLabel);

                if (item.label) item.label.text = txtLabel;
                else item.label = { text: txtLabel };
            } else item.label.text = AntLocalization.translateText(rItem.label);
        }

        var editorConfig = item.editorConfig;

        if (item.editorType === 'textArea')
            return (
                <TextArea
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={true}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    defaultValue={formData ? formData[item.dataField] : null}
                    onValueChanged={(e) => {
                        onFieldDataChanged({
                            [item.dataField]: e.value
                        });
                    }}
                    onFocusIn={(e) => {
                        let inputEl = e.element.querySelector('.dx-texteditor-input');
                        if (inputEl) {
                            inputEl.selectionStart = 0;
                            inputEl.selectionEnd = inputEl.value.length;
                        }
                        if (props.onFocusItem)
                            props.onFocusItem({
                                dataField: item.dataField
                            });
                    }}
                />
            );
        if (item.editorType === 'checkBox' || item.dataType === 'bool') {
            var obj = {};
            // if (props.labelLocation === 'right' || props.allCheckBoxLeft || item.checkBoxLeft) {
            obj.text = AntLocalization.translateText(item.label.oldText);
            obj.width = '100%';
            // }
            return (
                <CheckBox
                    ref={itemRef}
                    text={props.showPlaceholder !== false ? item.placeholder : undefined}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    {...obj}
                    defaultValue={formData ? formData[item.dataField] === 1 : false}
                    onValueChanged={(e) => {
                        onFieldDataChanged({
                            [item.dataField]: e.value ? 1 : 0
                        });
                    }}
                    onFocusIn={(e) => {
                        let inputEl = e.element.querySelector('.dx-texteditor-input');
                        if (inputEl) {
                            inputEl.selectionStart = 0;
                            inputEl.selectionEnd = inputEl.value.length;
                        }
                        if (props.onFocusItem)
                            props.onFocusItem({
                                dataField: item.dataField
                            });
                    }}
                />
            );
        }

        if (
            item.dataType === 'singleMonth' ||
            item.dataType === 'oneMonth' ||
            item.dataType === 'month' ||
            item.dataType === 'monthStart' ||
            item.dataType === 'monthEnd' ||
            item.editorType === 'antMonthBox'
        ) {
            var showClearButton = !item.disabled && (item.showClearButton !== undefined ? item.showClearButton : true);
            var showDropDownButton = !item.disabled && (item.showDropDownButton !== undefined ? item.showDropDownButton : true);

            return (
                <AntMonthBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={showClearButton}
                    showDropDownButton={showDropDownButton}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    disabled={item.disabled}
                    defaultValue={formData ? formData[item.dataField] : null}
                    // value={formData ? formData[item.dataField] : null}
                    onValueChanged={(e) => {
                        onFieldDataChanged({
                            [item.dataField]: e.value
                        });
                    }}
                    onFocusIn={(e) => {
                        if (props.onFocusItem)
                            props.onFocusItem({
                                dataField: item.dataField
                            });
                    }}
                ></AntMonthBox>
            );
        }

        if (
            item.dataType === 'singleYear' ||
            item.dataType === 'oneYear' ||
            item.dataType === 'year' ||
            item.dataType === 'yearStart' ||
            item.dataType === 'yearEnd' ||
            item.editorType === 'antYearBox'
        ) {
            var showClearButton = !item.disabled && (item.showClearButton !== undefined ? item.showClearButton : true);
            var showDropDownButton = !item.disabled && (item.showDropDownButton !== undefined ? item.showDropDownButton : true);

            return (
                <AntYearBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={showClearButton}
                    showDropDownButton={showDropDownButton}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    disabled={item.disabled}
                    defaultValue={formData ? formData[item.dataField] : null}
                    onValueChanged={(e) => {
                        onFieldDataChanged({
                            [item.dataField]: e.value
                        });
                    }}
                    onFocusIn={(e) => {
                        if (props.onFocusItem)
                            props.onFocusItem({
                                dataField: item.dataField
                            });
                    }}
                ></AntYearBox>
            );
        }
        if (item.dataType === 'date') {
            var strFormat = item.format;
            if (!strFormat) {
                strFormat = function (value) {
                    if (!value) return '';

                    var unixValue = AntTime.fromDate(value).unix();

                    if (AntTime.isUnixWithinToDay(unixValue))
                        return AntLocalization.translateText({ vi: 'Hôm nay', en: 'Today', fr: 'Aujourd\'hui', es: 'Hoy', id: 'Hari ini', ja: '今日', zh: '今天', ko: '오늘', hi: 'आज', ar: 'اليوم' });

                    if (AntTime.isUnixWithinYesterday(unixValue))
                        return AntLocalization.translateText({ vi: 'Hôm qua', en: 'Yesterday', fr: 'Hier', es: 'Ayer', id: 'Kemarin', ja: '昨日', zh: '昨天', ko: '어제', hi: 'कल (बीता)', ar: 'أمس' });

                    if (AntTime.isUnixWithinTomorrow(unixValue))
                        return AntLocalization.translateText({ vi: 'Ngày mai', en: 'Tomorrow', fr: 'Demain', es: 'Mañana', id: 'Besok', ja: '明日', zh: '明天', ko: '내일', hi: 'कल (आने वाला)', ar: 'غداً' });

                    if (AntTime.isUnixWithinCurrentYear(unixValue)) return AntTime.fromUnix(unixValue).format('DD/MM');

                    return AntTime.fromUnix(unixValue).format('DD/MM/YYYY');
                };
            }
            return (
                <DateBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={true}
                    showAnalogClock={false}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    defaultValue={formData && formData[item.dataField] ? AntTime.dateFromUnix(formData[item.dataField]) : null}
                    type={'date'}
                    displayFormat={strFormat}
                    onValueChanged={(e) => {
                        var value = e.value ? AntTime.unix(e.value) : null;

                        onFieldDataChanged({
                            [item.dataField]: value
                        });
                    }}
                />
            );
        }

        if (item.dataType === 'datetime') {
            var strFormat = item.format;
            if (!strFormat) {
                strFormat = function (value) {
                    if (!value) return '';

                    var unixValue = AntTime.fromDate(value).unix();

                    if (AntTime.isUnixWithinToDay(unixValue)) return AntTime.fromUnix(unixValue).format('hh:mm A');

                    // if (AntTime.isUnixWithinYesterday(unixValue)) return 'Hôm qua ' + AntTime.fromUnix(unixValue).format('HH:mm');
                    if (AntTime.isUnixWithinYesterday(unixValue))
                        return AntLocalization.translateText({ vi: 'Hôm qua', en: 'Yesterday', fr: 'Hier', es: 'Ayer', id: 'Kemarin', ja: '昨日', zh: '昨天', ko: '어제', hi: 'कल (बीता)', ar: 'أمس' });

                    if (AntTime.isUnixWithinTomorrow(unixValue))
                        return AntLocalization.translateText({ vi: 'Ngày mai', en: 'Tomorrow', fr: 'Demain', es: 'Mañana', id: 'Besok', ja: '明日', zh: '明天', ko: '내일', hi: 'कल (आने वाला)', ar: 'غداً' });

                    if (AntTime.isUnixWithinCurrentYear(unixValue)) return AntTime.fromUnix(unixValue).format('DD/MM HH:mm');

                    return AntTime.fromUnix(unixValue).format('DD/MM/YYYY');
                };
            }
            return (
                <DateBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={true}
                    showAnalogClock={false}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    defaultValue={formData && formData[item.dataField] ? AntTime.dateFromUnix(formData[item.dataField]) : null}
                    type={'datetime'}
                    displayFormat={strFormat}
                    onValueChanged={(e) => {
                        var value = e.value ? AntTime.unix(e.value) : null;

                        onFieldDataChanged({
                            [item.dataField]: value
                        });
                    }}
                />
            );
        }

        if (item.dataType === 'time') {
            return (
                <DateBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={true}
                    showAnalogClock={false}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    defaultValue={formData && formData[item.dataField] ? AntTime.dateFromUnix(formData[item.dataField]) : null}
                    type={'time'}
                    displayFormat={item.format ? item.format : 'HH:mm'}
                    onValueChanged={(e) => {
                        var value = e.value ? AntTime.timeUnix(AntTime.unix(e.value)) : null;

                        onFieldDataChanged({
                            [item.dataField]: value
                        });
                    }}
                />
            );
        }

        if (item.dataType === 'dateStart') {
            return (
                <DateBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={true}
                    showAnalogClock={false}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    defaultValue={
                        formData && typeof formData[item.dataField] === 'number'
                            ? AntTime.startOfDayFromUnix(formData[item.dataField]).toDate()
                            : null
                    }
                    type={'date'}
                    displayFormat={item.format ? item.format : 'dd/MM/yyyy'}
                    onValueChanged={(e) => {
                        var value = e.value ? AntTime.startOfDay(e.value).unix() : null;

                        onFieldDataChanged({
                            [item.dataField]: value
                        });
                    }}
                />
            );
        }

        if (item.dataType === 'dateEnd') {
            return (
                <DateBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={true}
                    showAnalogClock={false}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    defaultValue={
                        formData && typeof formData[item.dataField] === 'number' ? AntTime.endOfDayFromUnix(formData[item.dataField]).toDate() : null
                    }
                    type={'date'}
                    displayFormat={item.format ? item.format : 'dd/MM/yyyy'}
                    onValueChanged={(e) => {
                        var value = e.value ? AntTime.endOfDay(e.value).unix() : null;

                        onFieldDataChanged({
                            [item.dataField]: value
                        });
                    }}
                />
            );
        }

        if (String(item.dataField).endsWith('adm1_code')) {
            if (AddressData.getMode() === 'postal') return null; // nước postal (vd SG) — không cascade hành chính
            return (
                <AntSelectBox
                    ref={itemRef}
                    formRef={form}
                    dataField={item.dataField}
                    name={item.name}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    API={props.API}
                    isRemote={false}
                    defaultValue={formData ? formData[item.dataField] : null}
                    showClearButton={!item.disabled}
                    options={{
                        ...item.options,
                        dataSource: AddressData.getProvinces(),
                        valueExpr: 'code',
                        displayExpr: 'name'
                    }}
                    focusStateEnabled={!Boolean(item.disabled)}
                    onValueChanged={(e) => {
                        var provinceCodeField = String(item.dataField);
                        var districtCodeField = String(item.dataField).replace('adm1_code', 'adm2_code');
                        var wardCodeField = String(item.dataField).replace('adm1_code', 'adm3_code');

                        var provinceNameField = String(provinceCodeField).replace('adm1_code', 'adm1_name');
                        var districtNameField = String(districtCodeField).replace('adm2_code', 'adm2_name');
                        var wardNameField = String(wardCodeField).replace('adm3_code', 'adm3_name');

                        onFieldDataChanged({
                            [provinceCodeField]: e.value,
                            [provinceNameField]: e.value ? e.rowData['name'] : null,
                            [districtCodeField]: null,
                            [districtNameField]: null,
                            [wardCodeField]: null,
                            [wardNameField]: null
                        });
                    }}
                />
            );
        }
        if (String(item.dataField).endsWith('adm2_code')) {
            if (!AddressData.isLoaded() || AddressData.getLevelCount() < 2) return null;
            var provinceField = String(item.dataField).replace('adm2_code', 'adm1_code');
            var adm1_code = formData ? formData[provinceField] : null;

            return (
                <AntSelectBox
                    ref={itemRef}
                    formRef={form}
                    dataField={item.dataField}
                    name={item.name}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    API={props.API}
                    isRemote={false}
                    defaultValue={formData ? formData[item.dataField] : null}
                    showClearButton={!item.disabled}
                    options={{
                        ...item.options,
                        dataSource: AddressData.getDistricts(adm1_code),
                        valueExpr: 'code',
                        displayExpr: 'name'
                    }}
                    focusStateEnabled={!Boolean(item.disabled)}
                    onValueChanged={(e) => {
                        var districtCodeField = String(item.dataField);
                        var wardCodeField = String(item.dataField).replace('adm2_code', 'adm3_code');

                        var districtNameField = String(districtCodeField).replace('adm2_code', 'adm2_name');
                        var wardNameField = String(wardCodeField).replace('adm3_code', 'adm3_name');

                        onFieldDataChanged({
                            [districtCodeField]: e.value,
                            [districtNameField]: e.value ? e.rowData['name'] : null,
                            [wardCodeField]: null,
                            [wardNameField]: null
                        });
                    }}
                />
            );
        }
        if (String(item.dataField).endsWith('adm3_code')) {
            if (!AddressData.isLoaded() || AddressData.getLevelCount() < 3) return null;
            var districtField = String(item.dataField).replace('adm3_code', 'adm2_code');
            var adm2_code = formData ? formData[districtField] : null;

            return (
                <AntSelectBox
                    ref={itemRef}
                    formRef={form}
                    dataField={item.dataField}
                    name={item.name}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    API={props.API}
                    isRemote={false}
                    defaultValue={formData ? formData[item.dataField] : null}
                    showClearButton={!item.disabled}
                    options={{
                        ...item.options,
                        dataSource: AddressData.getWards(adm2_code),
                        valueExpr: 'code',
                        displayExpr: 'name'
                    }}
                    focusStateEnabled={!Boolean(item.disabled)}
                    onValueChanged={(e) => {
                        var wardCodeField = String(item.dataField);
                        var wardNameField = String(wardCodeField).replace('adm3_code', 'adm3_name');

                        onFieldDataChanged({
                            [wardCodeField]: e.value,
                            [wardNameField]: e.value ? e.rowData['name'] : null
                        });
                    }}
                />
            );
        }

        if (item.editorType === 'antTagBox') {
            var dataSource = [];

            var url =
                item.options && item.options.url
                    ? item.options.url
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.url
                        ? editorConfig.lookup.url
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.url
                            ? fieldConfig.lookup.url
                            : null;

            var keyField =
                item.options && item.options.keyField
                    ? item.options.keyField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.keyField
                        ? editorConfig.lookup.keyField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.keyField
                            ? fieldConfig.lookup.keyField
                            : 'code';

            var valueField =
                item.options && item.options.valueField
                    ? item.options.valueField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.valueField
                        ? editorConfig.lookup.valueField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.valueField
                            ? fieldConfig.lookup.valueField
                            : 'code';

            var displayField =
                item.options && item.options.displayField
                    ? item.options.displayField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.displayField
                        ? editorConfig.lookup.displayField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.displayField
                            ? fieldConfig.lookup.displayField
                            : 'name';

            var tableName =
                item.options && item.options.table
                    ? item.options.table
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.table
                        ? editorConfig.lookup.table
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.table
                            ? fieldConfig.lookup.table
                            : null;

            var height =
                item.options && item.options.height !== undefined
                    ? item.options.height
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.height
                        ? editorConfig.lookup.height
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.height
                            ? fieldConfig.lookup.height
                            : null;
            if (!height) height = 250;

            var width =
                item.options && item.options.width !== undefined
                    ? item.options.width
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.width
                        ? editorConfig.lookup.width
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.width
                            ? fieldConfig.lookup.width
                            : undefined;

            var parentFields = item.options && item.options.parentFields instanceof Array ? item.options.parentFields : undefined;

            var refreshOnOpened =
                parentFields !== undefined
                    ? true
                    : item.options && item.options.refreshOnOpened !== undefined
                        ? item.options.refreshOnOpened
                        : editorConfig && editorConfig.lookup && editorConfig.lookup.refreshOnOpened !== undefined
                            ? editorConfig.lookup.refreshOnOpened
                            : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.refreshOnOpened !== undefined
                                ? fieldConfig.lookup.refreshOnOpened
                                : undefined;

            var searchConditions =
                item.options && item.options.searchConditions !== undefined
                    ? item.options.searchConditions
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.searchConditions
                        ? editorConfig.lookup.searchConditions
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.searchConditions
                            ? fieldConfig.lookup.searchConditions
                            : null;

            var lookupClientValues =
                item.options && item.options.values !== undefined
                    ? item.options.values
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.values
                        ? editorConfig.lookup.values
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.values
                            ? fieldConfig.lookup.values
                            : null;

            lookupClientValues =
                lookupClientValues instanceof Array && lookupClientValues.length > 0
                    ? lookupClientValues.map((o) => {
                        if (typeof o === 'string') return { code: o };
                        return o;
                    })
                    : typeof lookupClientValues === 'object' && lookupClientValues !== null
                        ? [lookupClientValues]
                        : typeof lookupClientValues === 'string' && lookupClientValues !== null
                            ? [{ code: lookupClientValues }]
                            : [];

            lookupClientValues = lookupClientValues.filter((v) => AntCondition.isVisible(formData, v));

            if (fieldConfig && fieldConfig.lookup && fieldConfig.lookup.values) {
                if (item.options && item.options.values instanceof Array) {
                    lookupClientValues = lookupClientValues.map((o) => {
                        let found = item.options.values.find((f) => f.code === o.code);
                        if (found) return { ...found, ...o };
                        else return o;
                    });
                }
                if (fieldConfig.lookup && fieldConfig.lookup.values instanceof Array) {
                    lookupClientValues = lookupClientValues.map((o) => {
                        let found = fieldConfig.lookup.values.find((f) => f.code === o.code);
                        if (found) return { ...found, ...o };
                        else return o;
                    });
                }
            }

            var lookupServerName =
                item.options && item.options.lookupServerName !== undefined
                    ? item.options.lookupServerName
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.serverName
                        ? editorConfig.lookup.serverName
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.serverName
                            ? fieldConfig.lookup.serverName
                            : item.dataField;

            var lookupClientName =
                item.options && item.options.lookupClientName !== undefined
                    ? item.options.lookupClientName
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.clientName
                        ? editorConfig.lookup.clientName
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.clientName
                            ? fieldConfig.lookup.clientName
                            : item.dataField;

            var isRemote =
                item.options && item.options.isRemote !== undefined
                    ? item.options.isRemote
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.isRemote !== undefined
                        ? editorConfig.lookup.isRemote
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.isRemote !== undefined
                            ? fieldConfig.lookup.isRemote
                            : Boolean(url);

            var remotePaging =
                item.options && item.options.remotePaging !== undefined
                    ? item.options.remotePaging
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.remotePaging !== undefined
                        ? editorConfig.lookup.remotePaging
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.remotePaging !== undefined
                            ? fieldConfig.lookup.remotePaging
                            : true;

            if (!isRemote) {
                if (lookupClientValues) dataSource = lookupClientValues;

                if (remoteLookups instanceof Array && lookupServerName) {
                    var found = remoteLookups.find((l) => l.name === lookupServerName);

                    if (found && found.values instanceof Array) {
                        found = JSON.parse(stringify(found));
                        var remoteDS = found.values
                            .filter((v) => Boolean(v))
                            .map((v) => {
                                if (typeof v === 'object') return { ...v };
                                else return { code: v };
                            });

                        for (let i = 0; i < remoteDS.length; i++) {
                            var remoteValue = remoteDS[i];
                            var clientValue = dataSource.find((d) => d.code === remoteValue.code);
                            if (clientValue) {
                                Object.keys(clientValue).forEach((k) => {
                                    if (remoteValue[k] === undefined) remoteValue[k] = clientValue[k];
                                });
                            }
                        }
                        dataSource = remoteDS;
                    }
                }

                if (searchConditions)
                    dataSource = dataSource.filter((value) =>
                        AntCondition.isValid(value, {
                            when: { ...searchConditions }
                        })
                    );

                searchBar = false;
            }

            var isRequired = false;
            if (item.validationRules instanceof Array) isRequired = item.validationRules.find((r) => r.type === 'required');

            var allowNew = false;
            // var allowNew =
            //     item.options && item.options.allowNew !== undefined
            //         ? item.options.allowNew
            //         : editorConfig && editorConfig.lookup && editorConfig.lookup.allowNew !== undefined
            //         ? editorConfig.lookup.allowNew
            //         : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.allowNew !== undefined
            //         ? fieldConfig.lookup.allowNew
            //         : false;

            var showClearButton =
                // !item.disabled &&
                item.options && item.options.showClearButton !== undefined
                    ? item.options.showClearButton
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.showClearButton !== undefined
                        ? editorConfig.lookup.showClearButton
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.showClearButton !== undefined
                            ? fieldConfig.lookup.showClearButton
                            : !isRequired;

            var ordering =
                item.options && item.options.ordering !== undefined
                    ? item.options.ordering
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.ordering
                        ? editorConfig.lookup.ordering
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.ordering
                            ? fieldConfig.lookup.ordering
                            : null;

            return (
                <AntTagBox
                    ref={itemRef}
                    formRef={form}
                    dataField={item.dataField}
                    name={item.name}
                    formDisplayField={formDisplayField}
                    API={props.API}
                    url={url}
                    isRemote={isRemote}
                    remotePaging={remotePaging}
                    refreshOnOpened={refreshOnOpened}
                    lookupName={lookupServerName}
                    searchConditions={searchConditions}
                    defaultValue={formData ? formData[item.dataField] : null}
                    parentFields={parentFields}
                    acceptCustomValue={allowNew}
                    showClearButton={showClearButton}
                    height={height}
                    width={width}
                    ordering={ordering}
                    options={{
                        ...item.options,
                        table: tableName,
                        dataSource: dataSource,
                        keyExpr: keyField,
                        valueExpr: valueField,
                        displayExpr: displayField,
                        placeholder: props.showPlaceholder !== false ? item.placeholder : undefined,
                        noDataText: AntLocalization.translateText({ vi: 'Không có dữ liệu !', en: 'Have no data !', fr: 'Aucune donnée !', es: '¡Sin datos!', id: 'Tidak ada data!', ja: 'データがありません！', zh: '没有数据！', ko: '데이터 없음!', hi: 'कोई डेटा नहीं!', ar: 'لا توجد بيانات!' }),
                        stylingMode: item.options && item.options.stylingMode ? item.options.stylingMode : undefined,
                        className: item.options && item.options.className ? item.options.className : undefined
                    }}
                    focusStateEnabled={!Boolean(item.disabled)}
                    validationRules={item.disabled ? [] : item.validationRules}
                    getQueryFields={() => {
                        return mappings.map((m) => m.from);
                    }}
                    onValueChanged={(e) => {
                        var value = e.value instanceof Array && e.value.length <= 0 ? null : e.value;

                        onFieldDataChanged({
                            [item.dataField]: value
                        });
                    }}
                    onFocusIn={(e) => {
                        if (props.onFocusItem)
                            props.onFocusItem({
                                dataField: item.dataField
                            });
                    }}
                />
            );
        }

        if (item.editorType === 'antDropDownGrid') {
            var dataSource = [];

            var showLabel = props.showLabel !== undefined ? props.showLabel : false;
            var placeholder =
                props.showPlaceholder === false
                    ? null
                    : !showLabel && item.options && item.options.placeholder
                        ? item.options.placeholder
                        : item.placeholder
                            ? AntLocalization.translateText(item.placeholder)
                            : item.label && item.label.text
                                ? AntLocalization.translateText(item.label.text)
                                : editorConfig && editorConfig.placeholder
                                    ? AntLocalization.translateText(editorConfig.placeholder)
                                    : editorConfig && editorConfig.label
                                        ? AntLocalization.translateText(editorConfig.label)
                                        : fieldConfig && fieldConfig.placeholder
                                            ? AntLocalization.translateText(fieldConfig.placeholder)
                                            : fieldConfig && fieldConfig.label
                                                ? AntLocalization.translateText(fieldConfig.label)
                                                : null;

            var url =
                item.options && item.options.url
                    ? item.options.url
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.url
                        ? editorConfig.lookup.url
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.url
                            ? fieldConfig.lookup.url
                            : null;

            var keyField =
                item.options && item.options.keyField
                    ? item.options.keyField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.keyField
                        ? editorConfig.lookup.keyField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.keyField
                            ? fieldConfig.lookup.keyField
                            : null;

            var valueField =
                item.options && item.options.valueField
                    ? item.options.valueField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.valueField
                        ? editorConfig.lookup.valueField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.valueField
                            ? fieldConfig.lookup.valueField
                            : 'code';

            var displayField =
                item.options && item.options.displayField
                    ? item.options.displayField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.displayField
                        ? editorConfig.lookup.displayField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.displayField
                            ? fieldConfig.lookup.displayField
                            : 'name';

            var formDisplayField =
                item.options && item.options.formDisplayField
                    ? item.options.formDisplayField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.formDisplayField
                        ? editorConfig.lookup.formDisplayField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.formDisplayField
                            ? fieldConfig.lookup.formDisplayField
                            : item.displayField;
            if (!formDisplayField) {
                var strField = String(item.dataField).replace('_code', '_name');
                if (strField !== item.dataField) formDisplayField = strField;
                else {
                    if (formData && formData[displayField] !== undefined) formDisplayField = displayField;
                    else formDisplayField = item.dataField === 'code' ? 'name' : item.dataField;
                }
            }

            var tableName =
                item.options && item.options.table
                    ? item.options.table
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.table
                        ? editorConfig.lookup.table
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.table
                            ? fieldConfig.lookup.table
                            : null;

            var searchBar =
                item.options && item.options.searchBar !== undefined
                    ? item.options.searchBar
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.searchBar
                        ? editorConfig.lookup.searchBar
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.searchBar
                            ? fieldConfig.lookup.searchBar
                            : null;

            var columns =
                item.options && item.options.columns
                    ? item.options.columns
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.columns
                        ? editorConfig.lookup.columns
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.columns
                            ? fieldConfig.lookup.columns
                            : undefined;

            var height =
                item.options && item.options.height !== undefined
                    ? item.options.height
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.height
                        ? editorConfig.lookup.height
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.height
                            ? fieldConfig.lookup.height
                            : null;

            if (!height) {
                if (String(item.dataField).endsWith('code')) height = 300;
                else if (
                    (item.fieldConfig && item.fieldConfig.lookup && item.fieldConfig.lookup.values instanceof Array) ||
                    (item.options && item.options.values instanceof Array)
                )
                    height = 200;
                else height = 250;
            }

            var width =
                item.options && item.options.width !== undefined
                    ? item.options.width
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.width
                        ? editorConfig.lookup.width
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.width
                            ? fieldConfig.lookup.width
                            : null;

            var actionItems =
                item.options && item.options.actionItems instanceof Array
                    ? item.options.actionItems
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.actionItems instanceof Array
                        ? editorConfig.lookup.actionItems
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.actionItems instanceof Array
                            ? fieldConfig.lookup.actionItems
                            : undefined;

            var mappings =
                item.options && item.options.mapping instanceof Array
                    ? item.options.mapping
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.mapping instanceof Array
                        ? editorConfig.lookup.mapping
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.mapping instanceof Array
                            ? fieldConfig.lookup.mapping
                            : [];

            var mappingWhen =
                item.options && item.options.mappingWhen instanceof Array
                    ? item.options.mappingWhen
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.mappingWhen instanceof Array
                        ? editorConfig.lookup.mappingWhen
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.mappingWhen instanceof Array
                            ? fieldConfig.lookup.mappingWhen
                            : null;

            var mappingWhenType =
                item.options && item.options.mappingWhenType instanceof Array
                    ? item.options.mappingWhenType
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.mappingWhenType instanceof Array
                        ? editorConfig.lookup.mappingWhenType
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.mappingWhenType instanceof Array
                            ? fieldConfig.lookup.mappingWhenType
                            : null;

            var ordering =
                item.options && item.options.ordering instanceof Array
                    ? item.options.ordering
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.ordering instanceof Array
                        ? editorConfig.lookup.ordering
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.ordering instanceof Array
                            ? fieldConfig.lookup.ordering
                            : [];

            var parentFields = item.options && item.options.parentFields instanceof Array ? item.options.parentFields : undefined;

            var refreshOnOpened =
                parentFields !== undefined
                    ? true
                    : item.options && item.options.refreshOnOpened !== undefined
                        ? item.options.refreshOnOpened
                        : editorConfig && editorConfig.lookup && editorConfig.lookup.refreshOnOpened !== undefined
                            ? editorConfig.lookup.refreshOnOpened
                            : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.refreshOnOpened !== undefined
                                ? fieldConfig.lookup.refreshOnOpened
                                : undefined;

            var searchConditions =
                item.options && item.options.searchConditions !== undefined
                    ? item.options.searchConditions
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.searchConditions
                        ? editorConfig.lookup.searchConditions
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.searchConditions
                            ? fieldConfig.lookup.searchConditions
                            : null;

            var lookupClientValues =
                item.options && item.options.values !== undefined
                    ? item.options.values
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.values
                        ? editorConfig.lookup.values
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.values
                            ? fieldConfig.lookup.values
                            : null;

            lookupClientValues =
                lookupClientValues instanceof Array && lookupClientValues.length > 0
                    ? lookupClientValues.map((o) => {
                        if (typeof o === 'string') return { code: o };
                        return o;
                    })
                    : typeof lookupClientValues === 'object' && lookupClientValues !== null
                        ? [lookupClientValues]
                        : typeof lookupClientValues === 'string' && lookupClientValues !== null
                            ? [{ code: lookupClientValues }]
                            : [];

            lookupClientValues = lookupClientValues.filter((v) => AntCondition.isVisible(formData, v));

            if (fieldConfig && fieldConfig.lookup && fieldConfig.lookup.values) {
                if (item.options && item.options.values instanceof Array) {
                    lookupClientValues = lookupClientValues.map((o) => {
                        let found = item.options.values.find((f) => f.code === o.code);
                        if (found) return { ...found, ...o };
                        else return o;
                    });
                }
                if (fieldConfig.lookup && fieldConfig.lookup.values instanceof Array) {
                    lookupClientValues = lookupClientValues.map((o) => {
                        let found = fieldConfig.lookup.values.find((f) => f.code === o.code);
                        if (found) return { ...found, ...o };
                        else return o;
                    });
                }
            }

            var lookupServerName =
                item.options && item.options.lookupServerName !== undefined
                    ? item.options.lookupServerName
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.serverName
                        ? editorConfig.lookup.serverName
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.serverName
                            ? fieldConfig.lookup.serverName
                            : item.dataField;

            var lookupClientName =
                item.options && item.options.lookupClientName !== undefined
                    ? item.options.lookupClientName
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.clientName
                        ? editorConfig.lookup.clientName
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.clientName
                            ? fieldConfig.lookup.clientName
                            : item.dataField;

            var isRemote =
                item.options && item.options.isRemote !== undefined
                    ? item.options.isRemote
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.isRemote !== undefined
                        ? editorConfig.lookup.isRemote
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.isRemote !== undefined
                            ? fieldConfig.lookup.isRemote
                            : Boolean(url);

            var remotePaging =
                item.options && item.options.remotePaging !== undefined
                    ? item.options.remotePaging
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.remotePaging !== undefined
                        ? editorConfig.lookup.remotePaging
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.remotePaging !== undefined
                            ? fieldConfig.lookup.remotePaging
                            : true;

            if (!isRemote) {
                if (lookupClientValues) dataSource = lookupClientValues;

                if (remoteLookups instanceof Array && lookupServerName) {
                    var found = remoteLookups.find((l) => l.name === lookupServerName);

                    if (found && found.values instanceof Array) {
                        found = JSON.parse(stringify(found));
                        for (let i = 0; i < found.values.length; i++) {
                            var newValue = found.values[i];
                            if (newValue.code) {
                                var oldValue = dataSource.find((d) => d.code === newValue.code);
                                if (oldValue) {
                                    Object.keys(newValue).forEach((k) => {
                                        if (oldValue[k] === undefined) oldValue[k] = newValue[k];
                                    });
                                } else dataSource.push(newValue);
                            } else dataSource.push(newValue);
                        }
                    }
                }
                if (searchConditions)
                    dataSource = dataSource.filter((value) =>
                        AntCondition.isValid(value, {
                            when: { ...searchConditions }
                        })
                    );

                searchBar = false;
                if (!columns)
                    columns = [
                        {
                            dataField: 'name'
                        }
                    ];
            } else {
                if (!columns)
                    columns = [
                        {
                            dataField: item.dataField
                        }
                    ];
            }

            var isRequired = false;
            if (item.validationRules instanceof Array) isRequired = item.validationRules.find((r) => r.type === 'required');

            var showClearButton =
                !item.disabled &&
                (item.options && item.options.showClearButton !== undefined
                    ? item.options.showClearButton
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.showClearButton !== undefined
                        ? editorConfig.lookup.showClearButton
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.showClearButton !== undefined
                            ? fieldConfig.lookup.showClearButton
                            : !isRequired);

            var showDropDownButton =
                !item.disabled &&
                (item.options && item.options.showDropDownButton !== undefined
                    ? item.options.showDropDownButton
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.showDropDownButton !== undefined
                        ? editorConfig.lookup.showDropDownButton
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.showDropDownButton !== undefined
                            ? fieldConfig.lookup.showDropDownButton
                            : true);

            var showActions =
                item.options && item.options.showActions !== undefined
                    ? item.options.showActions
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.showActions !== undefined
                        ? editorConfig.lookup.showActions
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.showActions !== undefined
                            ? fieldConfig.lookup.showActions
                            : true;

            var groupByField =
                item.options && item.options.groupByField !== undefined
                    ? item.options.groupByField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.groupByField
                        ? editorConfig.lookup.groupByField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.groupByField
                            ? fieldConfig.lookup.groupByField
                            : null;

            var ordering =
                item.options && item.options.ordering !== undefined
                    ? item.options.ordering
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.ordering
                        ? editorConfig.lookup.ordering
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.ordering
                            ? fieldConfig.lookup.ordering
                            : null;

            return (
                <AntDropDownGrid
                    ref={itemRef}
                    formRef={form}
                    settingsName={editorConfig && editorConfig.name ? editorConfig.name : undefined}
                    dataField={item.dataField}
                    name={item.name}
                    formDisplayField={formDisplayField}
                    groupByField={groupByField}
                    placeholder={placeholder}
                    API={props.API}
                    url={url}
                    isRemote={isRemote}
                    remotePaging={remotePaging}
                    refreshOnOpened={refreshOnOpened}
                    lookupName={lookupServerName}
                    searchConditions={searchConditions}
                    ordering={ordering}
                    value={formData ? formData[item.dataField] : null}
                    parentFields={parentFields}
                    showClearButton={showClearButton}
                    showDropDownButton={showDropDownButton}
                    height={height}
                    width={width}
                    gridOptions={{
                        ...item.options,
                        table: tableName,
                        dataSource: dataSource,
                        keyExpr: keyField,
                        valueExpr: valueField,
                        displayExpr: displayField,
                        columns: columns,
                        searchBar: searchBar,
                        showActions: showActions,
                        actionItems: actionItems,
                        placeholder: placeholder,
                        noDataText: AntLocalization.translateText({ vi: 'Không có dữ liệu !', en: 'Have no data !', fr: 'Aucune donnée !', es: '¡Sin datos!', id: 'Tidak ada data!', ja: 'データがありません！', zh: '没有数据！', ko: '데이터 없음!', hi: 'कोई डेटा नहीं!', ar: 'لا توجد بيانات!' })
                    }}
                    focusStateEnabled={!Boolean(item.disabled)}
                    getQueryFields={() => {
                        return mappings.map((m) => m.from);
                    }}
                    onValueChanged={(e) => {
                        var preData = formData ? JSON.parse(stringify(formData)) : {};
                        preData[item.dataField] = e.value;

                        if (e.rowData && formDisplayField && formDisplayField !== item.dataField) preData[formDisplayField] = e.rowData[displayField];

                        var obj = { [item.dataField]: e.value };
                        if (formDisplayField && displayField && formDisplayField !== item.dataField) {
                            obj[formDisplayField] = e.value !== null && e.value !== undefined && e.rowData ? e.rowData[displayField] : null;
                        }

                        var isMapping = true;
                        if (mappingWhen && typeof mappingWhen === 'object') {
                            isMapping = AntCondition.isValid(preData, {
                                when: mappingWhen,
                                whenType: mappingWhenType
                            });
                        }

                        if (isMapping) {
                            for (let i = 0; i < mappings.length; i++) {
                                const m = mappings[i];

                                if (m.when && typeof m.when === 'object') {
                                    var mValid = AntCondition.isValid(preData, {
                                        when: m.when,
                                        whenType: m.whenType
                                    });
                                    if (!mValid) continue;
                                }

                                var sourceField = m.from;
                                var destField = m.to;
                                if (sourceField && destField) {
                                    if (
                                        e.rowData === null ||
                                        e.rowData === undefined ||
                                        e.rowData[sourceField] === null ||
                                        e.rowData[sourceField] === undefined
                                    ) {
                                        if (preData[destField] !== null && preData[destField] !== undefined) obj[destField] = null;
                                    } else {
                                        if (preData[destField] !== e.rowData[sourceField]) obj[destField] = e.rowData[sourceField];
                                    }
                                }

                                var setField = m.set;
                                var fieldValue = m.value;
                                if (setField && fieldValue !== undefined) {
                                    obj[setField] = fieldValue;
                                }
                            }
                        }

                        var keys = Object.keys(obj);
                        for (let i = 0; i < keys.length; i++) {
                            const k = keys[i];
                            args.component.itemOption(k, 'value', obj[k]);
                        }

                        if (keys.length > 0) onFieldDataChanged(obj);
                    }}
                />
            );
        }

        if (item.editorType === 'antDropDownTree') {
            var dataSource = [];

            var url =
                item.options && item.options.url
                    ? item.options.url
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.url
                        ? editorConfig.lookup.url
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.url
                            ? fieldConfig.lookup.url
                            : null;

            var valueField =
                item.options && item.options.valueField
                    ? item.options.valueField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.valueField
                        ? editorConfig.lookup.valueField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.valueField
                            ? fieldConfig.lookup.valueField
                            : 'code';

            var displayField =
                item.options && item.options.displayField
                    ? item.options.displayField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.displayField
                        ? editorConfig.lookup.displayField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.displayField
                            ? fieldConfig.lookup.displayField
                            : 'name';

            var formDisplayField =
                item.options && item.options.formDisplayField
                    ? item.options.formDisplayField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.formDisplayField
                        ? editorConfig.lookup.formDisplayField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.formDisplayField
                            ? fieldConfig.lookup.formDisplayField
                            : item.displayField;
            if (!formDisplayField) {
                var strField = String(item.dataField).replace('_code', '_name');
                if (strField !== item.dataField) formDisplayField = strField;
                else {
                    if (formData && formData[displayField] !== undefined) formDisplayField = displayField;
                    else formDisplayField = item.dataField === 'code' ? 'name' : item.dataField;
                }
            }

            var keyField =
                item.options && item.options.keyField
                    ? item.options.keyField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.keyField
                        ? editorConfig.lookup.keyField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.keyField
                            ? fieldConfig.lookup.keyField
                            : null;

            var parentField =
                item.options && item.options.parentField
                    ? item.options.parentField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.parentField
                        ? editorConfig.lookup.parentField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.parentField
                            ? fieldConfig.lookup.parentField
                            : null;

            var tableName =
                item.options && item.options.table
                    ? item.options.table
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.table
                        ? editorConfig.lookup.table
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.table
                            ? fieldConfig.lookup.table
                            : null;

            var searchBar =
                item.options && item.options.searchBar !== undefined
                    ? item.options.searchBar
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.searchBar
                        ? editorConfig.lookup.searchBar
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.searchBar
                            ? fieldConfig.lookup.searchBar
                            : null;

            var columns =
                item.options && item.options.columns
                    ? item.options.columns
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.columns
                        ? editorConfig.lookup.columns
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.columns
                            ? fieldConfig.lookup.columns
                            : undefined;

            var height =
                item.options && item.options.height !== undefined
                    ? item.options.height
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.height
                        ? editorConfig.lookup.height
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.height
                            ? fieldConfig.lookup.height
                            : null;

            if (!height) {
                if (String(item.dataField).endsWith('code')) height = 300;
                else height = 250;
            }

            var width =
                item.options && item.options.width !== undefined
                    ? item.options.width
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.width
                        ? editorConfig.lookup.width
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.width
                            ? fieldConfig.lookup.width
                            : null;

            var actionItems =
                item.options && item.options.actionItems instanceof Array
                    ? item.options.actionItems
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.actionItems instanceof Array
                        ? editorConfig.lookup.actionItems
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.actionItems instanceof Array
                            ? fieldConfig.lookup.actionItems
                            : undefined;

            var mappings =
                item.options && item.options.mapping instanceof Array
                    ? item.options.mapping
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.mapping instanceof Array
                        ? editorConfig.lookup.mapping
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.mapping instanceof Array
                            ? fieldConfig.lookup.mapping
                            : [];

            var mappingWhen =
                item.options && item.options.mappingWhen instanceof Array
                    ? item.options.mappingWhen
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.mappingWhen instanceof Array
                        ? editorConfig.lookup.mappingWhen
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.mappingWhen instanceof Array
                            ? fieldConfig.lookup.mappingWhen
                            : null;

            var mappingWhenType =
                item.options && item.options.mappingWhenType instanceof Array
                    ? item.options.mappingWhenType
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.mappingWhenType instanceof Array
                        ? editorConfig.lookup.mappingWhenType
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.mappingWhenType instanceof Array
                            ? fieldConfig.lookup.mappingWhenType
                            : null;

            var refreshOnOpened =
                item.options && item.options.refreshOnOpened !== undefined
                    ? item.options.refreshOnOpened
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.refreshOnOpened !== undefined
                        ? editorConfig.lookup.refreshOnOpened
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.refreshOnOpened !== undefined
                            ? fieldConfig.lookup.refreshOnOpened
                            : undefined;

            var searchConditions =
                item.options && item.options.searchConditions !== undefined
                    ? item.options.searchConditions
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.searchConditions
                        ? editorConfig.lookup.searchConditions
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.searchConditions
                            ? fieldConfig.lookup.searchConditions
                            : null;

            var lookupClientValues =
                item.options && item.options.values !== undefined
                    ? item.options.values
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.values
                        ? editorConfig.lookup.values
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.values
                            ? fieldConfig.lookup.values
                            : null;

            lookupClientValues =
                lookupClientValues instanceof Array && lookupClientValues.length > 0
                    ? lookupClientValues.map((o) => {
                        if (typeof o === 'string') return { code: o };
                        return o;
                    })
                    : typeof lookupClientValues === 'object' && lookupClientValues !== null
                        ? [lookupClientValues]
                        : typeof lookupClientValues === 'string' && lookupClientValues !== null
                            ? [{ code: lookupClientValues }]
                            : [];

            lookupClientValues = lookupClientValues.filter((v) => AntCondition.isVisible(formData, v));

            if (fieldConfig && fieldConfig.lookup && fieldConfig.lookup.values) {
                if (item.options && item.options.values instanceof Array) {
                    lookupClientValues = lookupClientValues.map((o) => {
                        let found = item.options.values.find((f) => f.code === o.code);
                        if (found) return { ...found, ...o };
                        else return o;
                    });
                }
                if (fieldConfig.lookup && fieldConfig.lookup.values instanceof Array) {
                    lookupClientValues = lookupClientValues.map((o) => {
                        let found = fieldConfig.lookup.values.find((f) => f.code === o.code);
                        if (found) return { ...found, ...o };
                        else return o;
                    });
                }
            }

            var lookupServerName =
                item.options && item.options.lookupServerName !== undefined
                    ? item.options.lookupServerName
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.serverName
                        ? editorConfig.lookup.serverName
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.serverName
                            ? fieldConfig.lookup.serverName
                            : item.dataField;

            var lookupClientName =
                item.options && item.options.lookupClientName !== undefined
                    ? item.options.lookupClientName
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.clientName
                        ? editorConfig.lookup.clientName
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.clientName
                            ? fieldConfig.lookup.clientName
                            : item.dataField;

            var isRemote =
                item.options && item.options.isRemote !== undefined
                    ? item.options.isRemote
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.isRemote !== undefined
                        ? editorConfig.lookup.isRemote
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.isRemote !== undefined
                            ? fieldConfig.lookup.isRemote
                            : Boolean(url);

            if (!isRemote) {
                if (lookupClientValues) dataSource = lookupClientValues;

                if (remoteLookups instanceof Array && lookupServerName) {
                    var found = remoteLookups.find((l) => l.name === lookupServerName);

                    if (found && found.values instanceof Array) {
                        found = JSON.parse(stringify(found));
                        for (let i = 0; i < found.values.length; i++) {
                            var newValue = found.values[i];
                            if (newValue.code) {
                                var oldValue = dataSource.find((d) => d.code === newValue.code);
                                if (oldValue) {
                                    Object.keys(newValue).forEach((k) => {
                                        if (oldValue[k] === undefined) oldValue[k] = newValue[k];
                                    });
                                } else dataSource.push(newValue);
                            } else dataSource.push(newValue);
                        }
                    }
                }
                if (searchConditions)
                    dataSource = dataSource.filter((value) =>
                        AntCondition.isValid(value, {
                            when: { ...searchConditions }
                        })
                    );

                searchBar = false;
                if (!columns)
                    columns = [
                        {
                            dataField: 'name'
                        }
                    ];
            } else {
                if (!columns)
                    columns = [
                        {
                            dataField: item.dataField
                        }
                    ];
            }

            var isRequired = false;
            if (item.validationRules instanceof Array) isRequired = item.validationRules.find((r) => r.type === 'required');

            var showClearButton =
                !item.disabled &&
                (item.options && item.options.showClearButton !== undefined
                    ? item.options.showClearButton
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.showClearButton !== undefined
                        ? editorConfig.lookup.showClearButton
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.showClearButton !== undefined
                            ? fieldConfig.lookup.showClearButton
                            : !isRequired);

            var showDropDownButton =
                !item.disabled &&
                (item.options && item.options.showDropDownButton !== undefined
                    ? item.options.showDropDownButton
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.showDropDownButton !== undefined
                        ? editorConfig.lookup.showDropDownButton
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.showDropDownButton !== undefined
                            ? fieldConfig.lookup.showDropDownButton
                            : true);
            return (
                <AntDropDownTree
                    ref={itemRef}
                    formRef={form}
                    dataField={item.dataField}
                    name={item.name}
                    formDisplayField={formDisplayField}
                    API={props.API}
                    url={url}
                    isRemote={isRemote}
                    remotePaging={remotePaging}
                    refreshOnOpened={refreshOnOpened}
                    lookupName={lookupServerName}
                    searchConditions={searchConditions}
                    value={formData ? formData[item.dataField] : null}
                    showClearButton={showClearButton}
                    showDropDownButton={showDropDownButton}
                    editorOptions={{
                        stylingMode: item.options && item.options.stylingMode ? item.options.stylingMode : undefined,
                        className: item.options && item.options.className ? item.options.className : undefined
                    }}
                    height={height}
                    width={width}
                    treeOptions={{
                        ...item.options,
                        table: tableName,
                        dataSource: dataSource,
                        valueExpr: valueField,
                        displayExpr: displayField,
                        keyExpr: keyField,
                        parentIdExpr: parentField,
                        columns: columns,
                        searchBar: searchBar,
                        actionItems: actionItems,
                        noDataText: AntLocalization.translateText({ vi: 'Không có dữ liệu !', en: 'Have no data !', fr: 'Aucune donnée !', es: '¡Sin datos!', id: 'Tidak ada data!', ja: 'データがありません！', zh: '没有数据！', ko: '데이터 없음!', hi: 'कोई डेटा नहीं!', ar: 'لا توجد بيانات!' })
                    }}
                    focusStateEnabled={!Boolean(item.disabled)}
                    validationRules={item.disabled ? [] : item.validationRules}
                    getQueryFields={() => {
                        return mappings.map((m) => m.from);
                    }}
                    onValueChanged={(e) => {
                        var preData = formData ? JSON.parse(stringify(formData)) : {};
                        preData[item.dataField] = e.value;

                        if (e.rowData && formDisplayField && formDisplayField !== item.dataField) preData[formDisplayField] = e.rowData[displayField];

                        var obj = { [item.dataField]: e.value };
                        if (formDisplayField && displayField && formDisplayField !== item.dataField) {
                            obj[formDisplayField] = e.value !== null && e.value !== undefined && e.rowData ? e.rowData[displayField] : null;
                        }

                        var isMapping = true;
                        if (mappingWhen && typeof mappingWhen === 'object') {
                            isMapping = AntCondition.isValid(preData, {
                                when: mappingWhen,
                                whenType: mappingWhenType
                            });
                        }

                        if (isMapping) {
                            for (let i = 0; i < mappings.length; i++) {
                                const m = mappings[i];

                                if (m.when && typeof m.when === 'object') {
                                    var mValid = AntCondition.isValid(preData, {
                                        when: m.when,
                                        whenType: m.whenType
                                    });
                                    if (!mValid) continue;
                                }

                                var sourceField = m.from;
                                var destField = m.to;
                                if (sourceField && destField) {
                                    if (
                                        e.rowData === null ||
                                        e.rowData === undefined ||
                                        e.rowData[sourceField] === null ||
                                        e.rowData[sourceField] === undefined
                                    ) {
                                        if (preData[destField] !== null && preData[destField] !== undefined) obj[destField] = null;
                                    } else {
                                        if (preData[destField] !== e.rowData[sourceField]) obj[destField] = e.rowData[sourceField];
                                    }
                                }

                                var setField = m.set;
                                var fieldValue = m.value;
                                if (setField && fieldValue !== undefined) {
                                    obj[setField] = fieldValue;
                                }
                            }
                        }

                        var keys = Object.keys(obj);
                        for (let i = 0; i < keys.length; i++) {
                            const k = keys[i];
                            args.component.itemOption(k, 'value', obj[k]);
                        }

                        if (keys.length > 0) onFieldDataChanged(obj);
                    }}
                />
            );
        }

        if (item.editorType === 'dxSelectBox') {
            // dần bỏ

            var url =
                item.options && item.options.url
                    ? item.options.url
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.url
                        ? editorConfig.lookup.url
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.url
                            ? fieldConfig.lookup.url
                            : null;

            var isRemote =
                item.options && item.options.isRemote !== undefined
                    ? item.options.isRemote
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.isRemote !== undefined
                        ? editorConfig.lookup.isRemote
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.isRemote !== undefined
                            ? fieldConfig.lookup.isRemote
                            : Boolean(url);

            var searchConditions =
                item.options && item.options.searchConditions !== undefined
                    ? item.options.searchConditions
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.searchConditions
                        ? editorConfig.lookup.searchConditions
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.searchConditions
                            ? fieldConfig.lookup.searchConditions
                            : null;

            var lookupClientValues =
                item.options && item.options.values !== undefined
                    ? item.options.values
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.values
                        ? editorConfig.lookup.values
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.values
                            ? fieldConfig.lookup.values
                            : null;

            lookupClientValues =
                lookupClientValues instanceof Array && lookupClientValues.length > 0
                    ? lookupClientValues.map((o) => {
                        if (typeof o === 'string') return { code: o };
                        return o;
                    })
                    : typeof lookupClientValues === 'object' && lookupClientValues !== null
                        ? [lookupClientValues]
                        : typeof lookupClientValues === 'string' && lookupClientValues !== null
                            ? [{ code: lookupClientValues }]
                            : [];

            lookupClientValues = lookupClientValues.filter((v) => AntCondition.isVisible(formData, v));

            if (fieldConfig && fieldConfig.lookup && fieldConfig.lookup.values) {
                if (item.options && item.options.values instanceof Array) {
                    lookupClientValues = lookupClientValues.map((o) => {
                        let found = item.options.values.find((f) => f.code === o.code);
                        if (found) return { ...found, ...o };
                        else return o;
                    });
                }
                if (fieldConfig.lookup && fieldConfig.lookup.values instanceof Array) {
                    lookupClientValues = lookupClientValues.map((o) => {
                        let found = fieldConfig.lookup.values.find((f) => f.code === o.code);
                        if (found) return { ...found, ...o };
                        else return o;
                    });
                }
            }

            var lookupServerName =
                item.options && item.options.lookupServerName !== undefined
                    ? item.options.lookupServerName
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.serverName
                        ? editorConfig.lookup.serverName
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.serverName
                            ? fieldConfig.lookup.serverName
                            : item.dataField;

            var lookupClientName =
                item.options && item.options.lookupClientName !== undefined
                    ? item.options.lookupClientName
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.clientName
                        ? editorConfig.lookup.clientName
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.clientName
                            ? fieldConfig.lookup.clientName
                            : item.dataField;

            var allowNew =
                item.options && item.options.allowNew !== undefined
                    ? item.options.allowNew
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.allowNew !== undefined
                        ? editorConfig.lookup.allowNew
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.allowNew !== undefined
                            ? fieldConfig.lookup.allowNew
                            : false;

            var dataSource = [];
            if (!isRemote) {
                if (lookupClientValues) dataSource = lookupClientValues;

                if (remoteLookups instanceof Array && lookupServerName) {
                    var found = remoteLookups.find((l) => l.name === lookupServerName);

                    if (found && found.values instanceof Array) {
                        found = JSON.parse(stringify(found));
                        for (let i = 0; i < found.values.length; i++) {
                            var newValue = found.values[i];
                            if (newValue.code) {
                                var oldValue = dataSource.find((d) => d.code === newValue.code);
                                if (oldValue) {
                                    Object.keys(newValue).forEach((k) => {
                                        if (oldValue[k] === undefined) oldValue[k] = newValue[k];
                                    });
                                } else dataSource.push(newValue);
                            } else dataSource.push(newValue);
                        }
                    }
                }
                if (searchConditions)
                    dataSource = dataSource.filter((value) =>
                        AntCondition.isValid(value, {
                            when: { ...searchConditions }
                        })
                    );
            }

            var showClearButton = !item.disabled && (item.showClearButton !== undefined ? item.showClearButton : true);
            var showDropDownButton = allowNew ? false : !item.disabled && (item.showDropDownButton !== undefined ? item.showDropDownButton : true);

            return (
                <SelectBox
                    ref={itemRef}
                    items={dataSource}
                    acceptCustomValue={allowNew}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={showClearButton}
                    showDropDownButton={showDropDownButton}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    noDataText={AntLocalization.translateText({ vi: 'Không có dữ liệu !', en: 'Have no data !', fr: 'Aucune donnée !', es: '¡Sin datos!', id: 'Tidak ada data!', ja: 'データがありません！', zh: '没有数据！', ko: '데이터 없음!', hi: 'कोई डेटा नहीं!', ar: 'لا توجد بيانات!' })}
                    defaultValue={formData ? formData[item.dataField] : null}
                    onValueChanged={(e) => {
                        onFieldDataChanged({
                            [item.dataField]: e.value
                        });
                    }}
                    onFocusIn={(e) => {
                        let inputEl = e.element.querySelector('.dx-texteditor-input');
                        if (inputEl) {
                            inputEl.selectionStart = 0;
                            inputEl.selectionEnd = inputEl.value.length;
                        }
                        if (props.onFocusItem)
                            props.onFocusItem({
                                dataField: item.dataField
                            });
                    }}
                />
            );
        }

        if (item.editorType === 'antSelectBox') {
            var dataSource = [];

            var url =
                item.options && item.options.url
                    ? item.options.url
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.url
                        ? editorConfig.lookup.url
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.url
                            ? fieldConfig.lookup.url
                            : null;

            var keyField =
                item.options && item.options.keyField
                    ? item.options.keyField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.keyField
                        ? editorConfig.lookup.keyField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.keyField
                            ? fieldConfig.lookup.keyField
                            : 'code';

            var valueField =
                item.options && item.options.valueField
                    ? item.options.valueField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.valueField
                        ? editorConfig.lookup.valueField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.valueField
                            ? fieldConfig.lookup.valueField
                            : 'code';

            var displayField =
                item.options && item.options.displayField
                    ? item.options.displayField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.displayField
                        ? editorConfig.lookup.displayField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.displayField
                            ? fieldConfig.lookup.displayField
                            : 'name';

            var formDisplayField =
                item.options && item.options.formDisplayField
                    ? item.options.formDisplayField
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.formDisplayField
                        ? editorConfig.lookup.formDisplayField
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.formDisplayField
                            ? fieldConfig.lookup.formDisplayField
                            : item.displayField;
            if (!formDisplayField) {
                var strField = String(item.dataField).replace('_code', '_name');
                if (strField !== item.dataField) formDisplayField = strField;
                else {
                    if (formData && formData[displayField] !== undefined) formDisplayField = displayField;
                    else formDisplayField = item.dataField === 'code' ? 'name' : item.dataField;
                }
            }

            var tableName =
                item.options && item.options.table
                    ? item.options.table
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.table
                        ? editorConfig.lookup.table
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.table
                            ? fieldConfig.lookup.table
                            : null;

            var height =
                item.options && item.options.height !== undefined
                    ? item.options.height
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.height
                        ? editorConfig.lookup.height
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.height
                            ? fieldConfig.lookup.height
                            : null;
            if (!height) height = 250;

            var width =
                item.options && item.options.width !== undefined
                    ? item.options.width
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.width
                        ? editorConfig.lookup.width
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.width
                            ? fieldConfig.lookup.width
                            : undefined;

            var mappings =
                item.options && item.options.mapping instanceof Array
                    ? item.options.mapping
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.mapping instanceof Array
                        ? editorConfig.lookup.mapping
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.mapping instanceof Array
                            ? fieldConfig.lookup.mapping
                            : [];

            var mappingWhen =
                item.options && item.options.mappingWhen instanceof Array
                    ? item.options.mappingWhen
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.mappingWhen instanceof Array
                        ? editorConfig.lookup.mappingWhen
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.mappingWhen instanceof Array
                            ? fieldConfig.lookup.mappingWhen
                            : null;

            var mappingWhenType =
                item.options && item.options.mappingWhenType instanceof Array
                    ? item.options.mappingWhenType
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.mappingWhenType instanceof Array
                        ? editorConfig.lookup.mappingWhenType
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.mappingWhenType instanceof Array
                            ? fieldConfig.lookup.mappingWhenType
                            : null;

            var parentFields = item.options && item.options.parentFields instanceof Array ? item.options.parentFields : undefined;

            var refreshOnOpened =
                parentFields !== undefined
                    ? true
                    : item.options && item.options.refreshOnOpened !== undefined
                        ? item.options.refreshOnOpened
                        : editorConfig && editorConfig.lookup && editorConfig.lookup.refreshOnOpened !== undefined
                            ? editorConfig.lookup.refreshOnOpened
                            : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.refreshOnOpened !== undefined
                                ? fieldConfig.lookup.refreshOnOpened
                                : undefined;

            var searchConditions =
                item.options && item.options.searchConditions !== undefined
                    ? item.options.searchConditions
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.searchConditions
                        ? editorConfig.lookup.searchConditions
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.searchConditions
                            ? fieldConfig.lookup.searchConditions
                            : null;

            var lookupClientValues =
                item.options && item.options.values !== undefined
                    ? item.options.values
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.values
                        ? editorConfig.lookup.values
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.values
                            ? fieldConfig.lookup.values
                            : null;

            lookupClientValues =
                lookupClientValues instanceof Array && lookupClientValues.length > 0
                    ? lookupClientValues.map((o) => {
                        if (typeof o === 'string') return { code: o };
                        return o;
                    })
                    : typeof lookupClientValues === 'object' && lookupClientValues !== null
                        ? [lookupClientValues]
                        : typeof lookupClientValues === 'string' && lookupClientValues !== null
                            ? [{ code: lookupClientValues }]
                            : [];

            lookupClientValues = lookupClientValues.filter((v) => AntCondition.isVisible(formData, v));

            if (fieldConfig && fieldConfig.lookup && fieldConfig.lookup.values) {
                if (item.options && item.options.values instanceof Array) {
                    lookupClientValues = lookupClientValues.map((o) => {
                        let found = item.options.values.find((f) => f.code === o.code);
                        if (found) return { ...found, ...o };
                        else return o;
                    });
                }
                if (fieldConfig.lookup && fieldConfig.lookup.values instanceof Array) {
                    lookupClientValues = lookupClientValues.map((o) => {
                        let found = fieldConfig.lookup.values.find((f) => f.code === o.code);
                        if (found) return { ...found, ...o };
                        else return o;
                    });
                }
            }

            var lookupServerName =
                item.options && item.options.lookupServerName !== undefined
                    ? item.options.lookupServerName
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.serverName
                        ? editorConfig.lookup.serverName
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.serverName
                            ? fieldConfig.lookup.serverName
                            : item.dataField;

            var lookupClientName =
                item.options && item.options.lookupClientName !== undefined
                    ? item.options.lookupClientName
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.clientName
                        ? editorConfig.lookup.clientName
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.clientName
                            ? fieldConfig.lookup.clientName
                            : item.dataField;

            var isRemote =
                item.options && item.options.isRemote !== undefined
                    ? item.options.isRemote
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.isRemote !== undefined
                        ? editorConfig.lookup.isRemote
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.isRemote !== undefined
                            ? fieldConfig.lookup.isRemote
                            : Boolean(url);

            var remotePaging =
                item.options && item.options.remotePaging !== undefined
                    ? item.options.remotePaging
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.remotePaging !== undefined
                        ? editorConfig.lookup.remotePaging
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.remotePaging !== undefined
                            ? fieldConfig.lookup.remotePaging
                            : true;

            if (!isRemote) {
                if (lookupClientValues) dataSource = lookupClientValues;

                if (remoteLookups instanceof Array && lookupServerName) {
                    var found = remoteLookups.find((l) => l.name === lookupServerName);

                    if (found && found.values instanceof Array) {
                        found = JSON.parse(stringify(found));
                        for (let i = 0; i < found.values.length; i++) {
                            var newValue = found.values[i];
                            if (newValue.code) {
                                var oldValue = dataSource.find((d) => d.code === newValue.code);
                                if (oldValue) {
                                    Object.keys(newValue).forEach((k) => {
                                        if (oldValue[k] === undefined) oldValue[k] = newValue[k];
                                    });
                                } else dataSource.push(newValue);
                            } else dataSource.push(newValue);
                        }
                    }
                }

                if (searchConditions)
                    dataSource = dataSource.filter((value) =>
                        AntCondition.isValid(value, {
                            when: { ...searchConditions }
                        })
                    );
            }

            var isRequired = false;
            if (item.validationRules instanceof Array) isRequired = item.validationRules.find((r) => r.type === 'required');

            var allowNew =
                item.options && item.options.allowNew !== undefined
                    ? item.options.allowNew
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.allowNew !== undefined
                        ? editorConfig.lookup.allowNew
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.allowNew !== undefined
                            ? fieldConfig.lookup.allowNew
                            : false;

            var showClearButton =
                // !item.disabled &&
                item.options && item.options.showClearButton !== undefined
                    ? item.options.showClearButton
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.showClearButton !== undefined
                        ? editorConfig.lookup.showClearButton
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.showClearButton !== undefined
                            ? fieldConfig.lookup.showClearButton
                            : !isRequired;

            var showDropDownButton = allowNew
                ? false
                : !item.disabled &&
                (item.options && item.options.showDropDownButton !== undefined
                    ? item.options.showDropDownButton
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.showDropDownButton !== undefined
                        ? editorConfig.lookup.showDropDownButton
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.showDropDownButton !== undefined
                            ? fieldConfig.lookup.showDropDownButton
                            : true);

            var ordering =
                item.options && item.options.ordering !== undefined
                    ? item.options.ordering
                    : editorConfig && editorConfig.lookup && editorConfig.lookup.ordering
                        ? editorConfig.lookup.ordering
                        : fieldConfig && fieldConfig.lookup && fieldConfig.lookup.ordering
                            ? fieldConfig.lookup.ordering
                            : null;
            return (
                <AntSelectBox
                    ref={itemRef}
                    formRef={form}
                    dataField={item.dataField}
                    name={item.name}
                    formDisplayField={formDisplayField}
                    API={props.API}
                    url={url}
                    isRemote={isRemote}
                    remotePaging={remotePaging}
                    refreshOnOpened={refreshOnOpened}
                    lookupName={lookupServerName}
                    searchConditions={searchConditions}
                    defaultValue={formData ? formData[item.dataField] : null}
                    parentFields={parentFields}
                    acceptCustomValue={allowNew}
                    showClearButton={showClearButton}
                    showDropDownButton={showDropDownButton}
                    height={height}
                    width={width}
                    ordering={ordering}
                    options={{
                        ...item.options,
                        table: tableName,
                        dataSource: dataSource,
                        keyExpr: keyField,
                        valueExpr: valueField,
                        displayExpr: displayField,
                        placeholder: props.showPlaceholder !== false ? item.placeholder : undefined,
                        noDataText: AntLocalization.translateText({ vi: 'Không có dữ liệu !', en: 'Have no data !', fr: 'Aucune donnée !', es: '¡Sin datos!', id: 'Tidak ada data!', ja: 'データがありません！', zh: '没有数据！', ko: '데이터 없음!', hi: 'कोई डेटा नहीं!', ar: 'لا توجد بيانات!' }),
                        stylingMode: item.options && item.options.stylingMode ? item.options.stylingMode : undefined,
                        className: item.options && item.options.className ? item.options.className : undefined
                    }}
                    focusStateEnabled={!Boolean(item.disabled)}
                    validationRules={item.disabled ? [] : item.validationRules}
                    getQueryFields={() => {
                        return mappings.map((m) => m.from);
                    }}
                    onValueChanged={(e) => {
                        var preData = formData ? JSON.parse(stringify(formData)) : {};
                        preData[item.dataField] = e.value;
                        if (e.rowData && formDisplayField && formDisplayField !== item.dataField) preData[formDisplayField] = e.rowData[displayField];

                        var obj = { [item.dataField]: e.value };
                        if (formDisplayField && displayField && formDisplayField !== item.dataField) {
                            obj[formDisplayField] = e.value !== null && e.value !== undefined && e.rowData ? e.rowData[displayField] : null;
                        }

                        var isMapping = true;
                        if (mappingWhen && typeof mappingWhen === 'object') {
                            isMapping = AntCondition.isValid(preData, {
                                when: mappingWhen,
                                whenType: mappingWhenType
                            });
                        }

                        if (isMapping) {
                            for (let i = 0; i < mappings.length; i++) {
                                const m = mappings[i];

                                if (m.when && typeof m.when === 'object') {
                                    var mValid = AntCondition.isValid(preData, {
                                        when: m.when,
                                        whenType: m.whenType
                                    });
                                    if (!mValid) continue;
                                }

                                var sourceField = m.from;
                                var destField = m.to;
                                if (sourceField && destField) {
                                    if (
                                        e.rowData === null ||
                                        e.rowData === undefined ||
                                        e.rowData[sourceField] === null ||
                                        e.rowData[sourceField] === undefined
                                    ) {
                                        if (preData[destField] !== null && preData[destField] !== undefined) obj[destField] = null;
                                    } else {
                                        if (preData[destField] !== e.rowData[sourceField]) obj[destField] = e.rowData[sourceField];
                                    }
                                }

                                var setField = m.set;
                                var fieldValue = m.value;
                                if (setField && fieldValue !== undefined) {
                                    obj[setField] = fieldValue;
                                }
                            }
                        }
                        var keys = Object.keys(obj);
                        for (let i = 0; i < keys.length; i++) {
                            const k = keys[i];
                            args.component.itemOption(k, 'value', obj[k]);
                        }

                        if (keys.length > 0) onFieldDataChanged(obj);
                    }}
                />
            );
        }

        if (item.dataType === 'float' || item.dataType === 'decimal') {
            var decDigit = item.decimalDigit !== undefined && item.decimalDigit !== null ? item.decimalDigit : 2;
            var strFormat = decDigit > 0 ? '#,##0.' + String('#').repeat(decDigit) : '#,##0';
            if (item.unitType === 'Km') strFormat = strFormat + ' Km';
            return (
                <NumberBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={true}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    alignment={'right'}
                    format={strFormat}
                    defaultValue={formData ? formData[item.dataField] : null}
                    onValueChanged={(e) => {
                        onFieldDataChanged({
                            [item.dataField]: e.value
                        });
                    }}
                    onFocusIn={(e) => {
                        let inputEl = e.element.querySelector('.dx-texteditor-input');
                        if (inputEl) {
                            inputEl.selectionStart = 0;
                            inputEl.selectionEnd = inputEl.value.length;
                        }
                        if (props.onFocusItem)
                            props.onFocusItem({
                                dataField: item.dataField
                            });
                    }}
                />
            );
        }

        if (item.dataType === 'day' || (item.dataType === 'int' && item.dataTypeDisplay == 'day')) {
            let strEnd = AntLocalization.translateText({ vi: 'ngày', en: 'day', fr: 'jour', es: 'día', id: 'hari', ja: '日', zh: '天', ko: '일', hi: 'दिन', ar: 'يوم' });
            let strFormat = '#,##0 ' + strEnd;
            return (
                <NumberBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={true}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    format={strFormat}
                    alignment={'right'}
                    defaultValue={formData ? formData[item.dataField] : null}
                    onValueChanged={(e) => {
                        onFieldDataChanged({
                            [item.dataField]: e.value
                        });
                    }}
                    onFocusIn={(e) => {
                        let inputEl = e.element.querySelector('.dx-texteditor-input');
                        if (inputEl) {
                            inputEl.selectionStart = 0;
                            inputEl.selectionEnd = inputEl.value.length;
                        }
                        if (props.onFocusItem)
                            props.onFocusItem({
                                dataField: item.dataField
                            });
                    }}
                >
                    <Validator validationRules={item.disabled ? [] : item.validationRules} dataField={item.dataField} />
                </NumberBox>
            );
        }

        if (item.dataType === 'int' && item.dataTypeDisplay === 'month') {
            let strEnd = AntLocalization.translateText({ vi: 'tháng', en: 'month', fr: 'mois', es: 'mes', id: 'bulan', ja: '月', zh: '个月', ko: '개월', hi: 'महीना', ar: 'شهر' });
            let strFormat = '#,##0 ' + strEnd;

            return (
                <NumberBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={true}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    format={strFormat}
                    alignment={'right'}
                    defaultValue={formData ? formData[item.dataField] : null}
                    onValueChanged={(e) => {
                        onFieldDataChanged({
                            [item.dataField]: e.value
                        });
                    }}
                    onFocusIn={(e) => {
                        let inputEl = e.element.querySelector('.dx-texteditor-input');
                        if (inputEl) {
                            inputEl.selectionStart = 0;
                            inputEl.selectionEnd = inputEl.value.length;
                        }
                        if (props.onFocusItem)
                            props.onFocusItem({
                                dataField: item.dataField
                            });
                    }}
                >
                    <Validator validationRules={item.disabled ? [] : item.validationRules} dataField={item.dataField} />
                </NumberBox>
            );
        }

        if (item.dataType === 'year' || (item.dataType === 'int' && item.dataTypeDisplay === 'year')) {
            let strEnd = AntLocalization.translateText({ vi: 'năm', en: 'year', fr: 'an', es: 'año', id: 'tahun', ja: '年', zh: '年', ko: '년', hi: 'वर्ष', ar: 'سنة' });
            let strFormat = '#,##0 ' + strEnd;

            return (
                <NumberBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={true}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    format={strFormat}
                    alignment={'right'}
                    defaultValue={formData ? formData[item.dataField] : null}
                    onValueChanged={(e) => {
                        onFieldDataChanged({
                            [item.dataField]: e.value
                        });
                    }}
                    onFocusIn={(e) => {
                        let inputEl = e.element.querySelector('.dx-texteditor-input');
                        if (inputEl) {
                            inputEl.selectionStart = 0;
                            inputEl.selectionEnd = inputEl.value.length;
                        }
                        if (props.onFocusItem)
                            props.onFocusItem({
                                dataField: item.dataField
                            });
                    }}
                >
                    <Validator validationRules={item.disabled ? [] : item.validationRules} dataField={item.dataField} />
                </NumberBox>
            );
        }

        if (item.dataType === 'period' || (item.dataType === 'int' && item.dataTypeDisplay === 'period')) {
            let strEnd = AntLocalization.translateText({ vi: 'kỳ', en: 'period', fr: 'période', es: 'período', id: 'periode', ja: '期間', zh: '期', ko: '기간', hi: 'अवधि', ar: 'فترة' });
            let strFormat = '#,##0 ' + strEnd;

            return (
                <NumberBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={true}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    format={strFormat}
                    alignment={'right'}
                    defaultValue={formData ? formData[item.dataField] : null}
                    onValueChanged={(e) => {
                        onFieldDataChanged({
                            [item.dataField]: e.value
                        });
                    }}
                    onFocusIn={(e) => {
                        let inputEl = e.element.querySelector('.dx-texteditor-input');
                        if (inputEl) {
                            inputEl.selectionStart = 0;
                            inputEl.selectionEnd = inputEl.value.length;
                        }
                        if (props.onFocusItem)
                            props.onFocusItem({
                                dataField: item.dataField
                            });
                    }}
                >
                    <Validator validationRules={item.disabled ? [] : item.validationRules} dataField={item.dataField} />
                </NumberBox>
            );
        }

        if (item.dataType === 'int' || item.dataType === 'fixedPoint')
            return (
                <NumberBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={true}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    format={{ type: 'fixedPoint' }}
                    alignment={'right'}
                    defaultValue={formData ? parseInt(formData[item.dataField]) : null}
                    onValueChanged={(e) => {
                        onFieldDataChanged({
                            [item.dataField]: e.value
                        });
                    }}
                    onFocusIn={(e) => {
                        let inputEl = e.element.querySelector('.dx-texteditor-input');
                        if (inputEl) {
                            inputEl.selectionStart = 0;
                            inputEl.selectionEnd = inputEl.value.length;
                        }
                        if (props.onFocusItem)
                            props.onFocusItem({
                                dataField: item.dataField
                            });
                    }}
                />
            );
        if (item.dataType === 'percent')
            return (
                <NumberBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={true}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    // format={{ type: 'percent', precision: item.decimalDigit }}
                    format={{ type: 'percent', precision: item.decimalDigit !== undefined ? item.decimalDigit : 0 }}
                    defaultValue={formData ? parseFloat(formData[item.dataField]) : null}
                    onValueChanged={(e) => {
                        onFieldDataChanged({
                            [item.dataField]: e.value
                        });
                    }}
                    onFocusIn={(e) => {
                        let inputEl = e.element.querySelector('.dx-texteditor-input');
                        if (inputEl) {
                            inputEl.selectionStart = 0;
                            inputEl.selectionEnd = inputEl.value.length;
                        }
                        if (props.onFocusItem)
                            props.onFocusItem({
                                dataField: item.dataField
                            });
                    }}
                />
            );

        if (item.dataType === 'currency') {
            return (
                <NumberBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={true}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    format={(value) => {
                        return AntLocalization.translateCurrency(
                            value,
                            item.showSign !== undefined
                                ? item.showSign
                                : item.fieldConfig && item.fieldConfig.showSign !== undefined
                                    ? item.fieldConfig.showSign
                                    : false
                        );
                    }}
                    alignment={'right'}
                    defaultValue={formData ? parseInt(formData[item.dataField]) : null}
                    onValueChanged={(e) => {
                        onFieldDataChanged({
                            [item.dataField]: e.value
                        });
                    }}
                    onFocusIn={(e) => {
                        let inputEl = e.element.querySelector('.dx-texteditor-input');
                        if (inputEl) {
                            inputEl.selectionStart = 0;
                            inputEl.selectionEnd = inputEl.value.length;
                        }
                        if (props.onFocusItem)
                            props.onFocusItem({
                                dataField: item.dataField
                            });
                    }}
                />
            );
        }
        if (item.dataType === 'phone')
            return (
                <TextBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={true}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    showMaskMode="onFocus"
                    mask={(function () { var c = AntLocalStorage.getSetting('Sys_BaseCurrency'); var m = { VND: 'X000 000 000', USD: '(000) 000-0000', THB: '000 000 0000', JPY: '000 0000 0000', GBP: '00000 000000' }; return m[c] || '0000000000000'; })()}
                    maskRules={(function () { var c = AntLocalStorage.getSetting('Sys_BaseCurrency'); return c === 'VND' ? { X: /[02-9]/ } : {}; })()}
                    alignment={'right'}
                    defaultValue={formData ? formData[item.dataField] : null}
                    onValueChanged={(e) => {
                        onFieldDataChanged(
                            {
                                [item.dataField]: e.value
                            },
                            true
                        );
                    }}
                    onFocusIn={(e) => {
                        let inputEl = e.element.querySelector('.dx-texteditor-input');
                        if (inputEl) {
                            inputEl.selectionStart = 0;
                            inputEl.selectionEnd = inputEl.value.length;
                        }
                        if (props.onFocusItem)
                            props.onFocusItem({
                                dataField: item.dataField
                            });
                    }}
                />
            );

        if (item.dataType === 'email')
            return (
                <TextBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    showClearButton={true}
                    {...item.options}
                    mode="email"
                    focusStateEnabled={!Boolean(item.disabled)}
                    defaultValue={formData ? formData[item.dataField] : null}
                    onValueChanged={(e) => {
                        onFieldDataChanged(
                            {
                                [item.dataField]: e.value
                            },
                            true
                        );
                    }}
                // onFocusIn={(e) => {
                //     let inputEl = e.element.querySelector('.dx-texteditor-input');
                // if (inputEl) {
                //     inputEl.selectionStart = 0;
                //     inputEl.selectionEnd = inputEl.value.length;
                // }
                // if (props.onFocusItem)
                // props.onFocusItem({
                //     dataField: item.dataField
                // });
                // }}
                />
            );

        if (item.dataType !== 'empty' && (item.editorType === 'dxTextBox' || item.editorType === undefined)) {
            var mode = 'text';
            if (item.dataType === 'password') mode = 'password';
            return (
                <TextBox
                    ref={itemRef}
                    placeholder={props.showPlaceholder !== false ? item.placeholder : undefined}
                    valueChangeEvent={'keyup'}
                    showClearButton={true}
                    {...item.options}
                    focusStateEnabled={!Boolean(item.disabled)}
                    mode={mode}
                    defaultValue={formData ? formData[item.dataField] : null}
                    onValueChanged={(e) => {
                        onFieldDataChanged(
                            {
                                [item.dataField]: e.value
                            },
                            true
                        );
                    }}
                    onFocusIn={(e) => {
                        let inputEl = e.element.querySelector('.dx-texteditor-input');
                        if (inputEl) {
                            inputEl.selectionStart = 0;
                            inputEl.selectionEnd = inputEl.value.length;
                        }
                        if (props.onFocusItem)
                            props.onFocusItem({
                                dataField: item.dataField
                            });
                    }}
                />
            );
        }
    });

    const onConditionsChanged = useCallback(() => {
        var obj = {};

        var rawObj = {};
        var search = formData.search;

        Object.keys(formData).forEach(function (fieldName) {
            var value = formData[fieldName];
            if (value !== undefined && value !== null && value !== '' && value != search) {
                rawObj[fieldName] = value;

                if (props.items instanceof Array) {
                    var strField = String(fieldName)
                        .replace('_oneMonth', '')
                        .replace('_oneYear', '')
                        .replace('_startDate', '')
                        .replace('_startMonth', '')
                        .replace('_startYear', '')
                        .replace('_endDate', '')
                        .replace('_endMonth', '')
                        .replace('_endYear', '');
                    var found = props.items.find((i) => i.dataField === strField);
                    if (!found) return;
                }

                obj[fieldName] = value;
            }
        });

        if (props.onBeforeGenerateConditions) obj = props.onBeforeGenerateConditions(formData, obj);

        if (obj && props.onConditionsChanged) props.onConditionsChanged(rawObj);
    }, [formData, props.items, props.onBeforeGenerateConditions, props.onConditionsChanged]);

    const onFieldDataChanged = useCallback(
        (args) => {
            var data = JSON.parse(stringify(args));
            var changedFields = Object.keys(data);
            var curObj = { ...formData, ...data };

            if (formItems.current instanceof Array) {
                formItems.current
                    .filter(
                        (item) =>
                            item.dataField &&
                            item.options &&
                            item.options.parentFields instanceof Array &&
                            item.options.parentFields.find((pField) => changedFields.includes(pField))
                    )
                    .forEach((item) => {
                        data[item.dataField] = null;
                    });

                var items = formItems.current
                    .filter(
                        (item) =>
                            item.dataField &&
                            ((item.formula && item.formula instanceof Array) ||
                                item.dataType === 'join' ||
                                item.dataType === 'fist' ||
                                item.dataType === 'last')
                    )
                    .map((item, index) => {
                        return {
                            ...item,
                            dataField: item.dataField,
                            formula: item.formula,
                            index: item.formulaIndex !== undefined ? item.formulaIndex : index
                        };
                    })
                    .sort((a, b) => (a.index > b.index ? 1 : -1));

                items.forEach((item) => {
                    var value;

                    if (item.formula instanceof Array) {
                        var fm = item.formula[0];
                        var defaultNullValue =
                            item.formula[1] !== undefined && item.formula[1] !== null && typeof item.formula[1] === 'number' ? item.formula[1] : 0;
                        value = Calculator.execNum(curObj, fm, defaultNullValue);
                    } else if (item.dataType === 'join') {
                        var fields = item.fields;
                        if (fields instanceof Array) {
                            value = fields
                                .map((f) => {
                                    if (!curObj) return null;

                                    return curObj[f];
                                })

                                .filter((f) => Boolean(f))
                                .map((f) => String(f))
                                .join(item.separator ? item.separator : ', ');
                        }
                    } else if (item.dataType === 'first') {
                        var fields = item.fields;
                        if (fields instanceof Array) {
                            value = fields
                                .map((f) => {
                                    if (!curObj) return null;

                                    return curObj[f];
                                })
                                .filter((f) => Boolean(f))
                                .map((f) => String(f))
                                .shift();
                        }
                    } else if (item.dataType === 'last') {
                        var fields = item.fields;
                        if (fields instanceof Array) {
                            value = fields
                                .map((f) => {
                                    if (!curObj) return null;

                                    return curObj[f];
                                })
                                .filter((f) => Boolean(f))
                                .map((f) => String(f))
                                .pop();
                        }
                    }

                    if (value !== undefined) {
                        curObj[item.dataField] = value;
                        // if (value !== data[item.dataField])

                        data[item.dataField] = value;
                    }
                });
            }

            if (Object.keys(data).length > 0) {
                setFormData((v) => Object.assign(v, data));

                onConditionsChanged();

                if (props.onFieldDataChanged) props.onFieldDataChanged(data);
                if (props.onFormDataChanged) {
                    let objForm = {};
                    Object.assign(objForm, formData);
                    Object.assign(objForm, data);
                    console.log('ant-log-objForm : ', objForm);
                    props.onFormDataChanged(objForm);
                }
            }
            if (props.onError) props.onError(null);
        },
        [formData, onConditionsChanged, props.onFieldDataChanged, props.onFormDataChanged, props.onError]
    );
    onFieldDataChangedRef.current = onFieldDataChanged;

    const resetUI = () => {
        // beginUpdate();
        if (form.current && form.current.instance) {
            resetDefaultValue();
            resetVisibility();
            resetDisabled();
            resetColSpan();
            form.current.instance.updateDimensions();
        }
        // endUpdate();
    };

    const onContentReady = (e) => {
        resetUI();
    };

    useEffect(() => {
        formItems.current = initFormItems();
        load();
    }, []);

    // useEffect(() => {
    //     setFormData(props.formData ? props.formData : {});
    // }, [props.formData]);

    useEffect(() => {
        if (prepared) {
            formItems.current = initFormItems();
            setFormData(props.formData ? props.formData : {});
        }
    }, [prepared]);

    // Envelope địa chỉ nạp xong (async) → dựng lại items để nhãn/placeholder cấp adm khớp nước,
    // và ẩn/hiện đúng số cấp. Bump tick để Form re-render đọc formItems.current mới.
    useEffect(() => {
        if (addrLoadTick > 0) {
            formItems.current = initFormItems();
            setAddrRenderTick((n) => n + 1);
        }
    }, [addrLoadTick]);

    useEffect(() => {
        var count = AntCondition.getValue(formData, props.colCount);
        if (count === undefined) count = props.colCountDefault;
        setColCount(count);
    }, [formData]);

    return (
        <React.Fragment>
            <Form
                className="ant-form ant-search-form"
                ref={form}
                formData={formData}
                colCount={colCount}
                alignItemLabels={props.alignItemLabels !== undefined ? props.alignItemLabels : false}
                alignItemLabelsInAllGroups={props.alignItemLabelsInAllGroups !== undefined ? props.alignItemLabelsInAllGroups : true}
                items={formItems.current}
                focusStateEnabled={false}
                onContentReady={onContentReady}
            >
                <Template name={'renderItem'} render={renderItem} />
            </Form>
        </React.Fragment>
    );
}
